<template>
  <!-- A client's or partition's page: details, plan and preferences (redesigned in US 9391) -->
  <div class="cp-page">
    <section class="home-hero cp-hero" qid="client-hero">
      <div class="cp-logo" :class="{ 'cp-logo--small': client.parent }">
        <img v-if="client.logo" :src="client.logo" alt="" qid="profile-photo">
        <span v-else>{{ initials(client.name) }}</span>
      </div>

      <div class="home-hero__main">
        <div class="home-hero__eyebrow">
          {{ client.parent ? $t('client_page.partition_of', { name: client.parent.name }) : $t('client_page.client') }}
        </div>
        <h1 class="home-hero__title" qid="p-view-page-title">{{ client.name }}</h1>
        <div class="pf-meta">
          <span v-if="client.cvr"><font-awesome-icon icon="building"/>{{ $t('client_page.cvr', { cvr: client.cvr }) }}</span>
          <span v-if="client.website"><font-awesome-icon icon="globe"/>{{ client.website }}</span>
          <span v-if="client.updated_at" qid="view-p-modified">
            <font-awesome-icon icon="clock"/>
            {{ client.updated_by
              ? $t('client_page.updated_by', { date: formatDate(client.updated_at), name: client.updated_by.name })
              : $t('client_page.updated', { date: formatDate(client.updated_at) }) }}
          </span>
        </div>
        <div class="home-hero__chips">
          <button v-if="checkIfAdmin()"
                  type="button"
                  class="home-chip home-chip--light"
                  qid="edit-p-button"
                  @click="showFormFunction">
            <font-awesome-icon icon="pen"/>{{ $t('client_page.edit_details') }}
          </button>
          <router-link v-if="!client.parent && isCurrentWorkspace && checkIfAdmin()"
                       :to="{ name: 'maintenance.partitions' }"
                       class="home-chip"
                       qid="client-new-partition">
            <font-awesome-icon icon="plus"/>{{ $t('client_page.new_partition') }}
          </router-link>
        </div>
      </div>

      <div v-if="!client.parent && client.id" class="pf-stats cp-stats" qid="client-stats">
        <div class="pf-stat">
          <b>{{ planName }}</b>
          <span>{{ $t('client_page.stats.plan') }}</span>
        </div>
        <div class="pf-stat">
          <b>{{ partitionRows.length }}</b>
          <span>{{ $t('client_page.stats.partitions') }}</span>
        </div>
        <div class="pf-stat">
          <b>{{ totalMembers }}</b>
          <span>{{ $t('client_page.stats.users') }}</span>
        </div>
        <div class="pf-stat">
          <b>{{ formatTokens(tokensThisMonth) }}</b>
          <span>{{ $t('client_page.stats.tokens', { month: monthName() }) }}</span>
        </div>
      </div>
      <router-link v-if="client.parent && parentIsMine"
                   :to="{ name: 'clientProfile', params: { slug: client.parent.slug } }"
                   class="home-chip cp-parent-link"
                   qid="client-go-to-parent">
        <font-awesome-icon icon="arrow-right"/>{{ $t('client_page.go_to', { name: client.parent.name }) }}
      </router-link>
    </section>

    <div class="cp-tabs" role="tablist">
      <button v-for="tab in tabs"
              :key="tab"
              type="button"
              role="tab"
              class="cp-tab"
              :class="{ 'is-on': section === tab }"
              :aria-selected="section === tab ? 'true' : 'false'"
              :qid="'client-tab-' + tab"
              @click="section = tab">
        {{ $t('client_page.tab.' + tab) }}
      </button>
    </div>

    <!-- Overview -->
    <div v-if="section === 'overview'" class="cp-overview">
      <div class="cp-stack">
        <div class="v2-card" qid="client-details">
          <div class="v2-card__head">
            <h3>{{ $t('client_page.details.title') }}</h3>
            <button v-if="checkIfAdmin()" type="button" class="us-link" qid="client-details-edit" @click="showFormFunction">
              <font-awesome-icon icon="pen"/>{{ $t('client_page.details.edit') }}
            </button>
          </div>
          <div class="v2-card__body">
            <dl class="pf-kv">
              <template v-for="field in detailFields">
                <dt :key="'k-' + field.key">{{ $t(field.label) }}</dt>
                <dd :key="'v-' + field.key" :class="{ 'is-empty': !field.value }">
                  <img v-if="field.key === 'country' && client.country_with_flag" :src="client.country_with_flag.flag" height="13" alt="">
                  {{ field.value || $t('client_page.details.not_set') }}
                </dd>
              </template>
            </dl>
          </div>
        </div>

        <div v-if="modules.length" class="v2-card" qid="client-modules">
          <div class="v2-card__head">
            <h3>{{ $t('client_page.modules.title') }}</h3>
          </div>
          <div class="v2-card__body cp-modules">
            <span v-for="module in modules" :key="module.id" class="v2-tag v2-tag--teal">
              <font-awesome-icon icon="check"/>{{ moduleName(module) }}
            </span>
          </div>
        </div>
      </div>

      <div class="cp-stack">
        <div v-if="!client.parent" class="v2-card" qid="client-partitions">
          <div class="v2-card__head">
            <h3>{{ $t('client_page.partitions.title') }} <span class="v2-card__sub">· {{ partitionRows.length }}</span></h3>
            <router-link v-if="isCurrentWorkspace" :to="{ name: 'maintenance.partitions' }" class="us-link">
              {{ $t('client_page.partitions.manage') }}<font-awesome-icon icon="chevron-right"/>
            </router-link>
          </div>
          <table v-if="partitionRows.length" class="us-table cp-usage-table">
            <thead>
              <tr>
                <th>{{ $t('client_page.partitions.partition') }}</th>
                <th class="cp-num">{{ $t('client_page.partitions.users') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in partitionRows.slice(0, 6)" :key="row.id">
                <td><div class="cp-who"><span class="pf-ws__logo">{{ initials(row.name) }}</span><span class="cp-who__name">{{ row.name }}</span></div></td>
                <td class="cp-num">{{ row.members }}</td>
              </tr>
              <tr v-if="partitionRows.length > 6">
                <td colspan="2" class="us-muted">{{ $t('client_page.partitions.more', { count: partitionRows.length - 6 }) }}</td>
              </tr>
            </tbody>
          </table>
          <div v-else class="v2-card__body pf-hint">{{ $t('client_page.partitions.none') }}</div>
        </div>

        <div v-else class="v2-card" qid="client-partition-info">
          <div class="v2-card__head"><h3>{{ $t('client_page.partition_info.title') }}</h3></div>
          <div class="v2-card__body">
            <dl class="pf-kv">
              <dt>{{ $t('client_page.partition_info.client') }}</dt>
              <dd>{{ client.parent.name }}</dd>
              <dt>{{ $t('client_page.partition_info.users') }}</dt>
              <dd>{{ ownMembers === null ? '–' : ownMembers }}</dd>
              <dt>{{ $t('client_page.partition_info.emails') }}</dt>
              <dd>{{ $t('client_page.notifications.mode.' + (client.email_mode || 'off')) }}</dd>
            </dl>
          </div>
        </div>
      </div>
    </div>

    <!-- Billing (clients only) -->
    <div v-if="section === 'billing' && !client.parent" class="cp-split">
      <nav class="cp-subnav">
        <button type="button" class="cp-subnav__item" :class="{ 'is-on': billingPage === 'plan' }" qid="client-billing-plan" @click="billingPage = 'plan'">
          <font-awesome-icon icon="file-invoice"/>{{ $t('client_page.billing.plan') }}
        </button>
        <button v-if="client.subscription_status != 'enterprise'" type="button" class="cp-subnav__item" :class="{ 'is-on': billingPage === 'payments' }" qid="client-billing-payments" @click="billingPage = 'payments'">
          <font-awesome-icon icon="file-download"/>{{ $t('client_page.billing.payments') }}
        </button>
        <button v-if="canSeeUsage" type="button" class="cp-subnav__item" :class="{ 'is-on': billingPage === 'ai_usage' }" qid="client-billing-ai-usage" @click="billingPage = 'ai_usage'">
          <font-awesome-icon icon="sparkles" class="cp-ai-icon"/>{{ $t('client_page.billing.ai_usage') }}
          <span class="cp-new">{{ $t('client_page.new') }}</span>
        </button>
      </nav>
      <div class="cp-split__main">
        <div v-if="billingPage === 'plan'" class="cp-legacy">
            <div class="v2-card cp-plan" qid="client-current-plan">
              <div class="cp-plan__head">
                <span class="cp-plan__icon"><font-awesome-icon :icon="planIcon"/></span>
                <div class="cp-plan__titles">
                  <div class="cp-plan__eyebrow">{{ $t('profile.client.billing.current_plan_title') }}</div>
                  <div class="cp-plan__name">{{ planDisplayName }}</div>
                  <div v-if="planDateText" class="cp-plan__date">{{ planDateText }}</div>
                </div>
                <span v-if="client.subscription_status && client.subscription_status !== 'enterprise'" class="v2-tag" :class="planStatusTag">
                  {{ $t('client_page.plan.status.' + client.subscription_status) }}
                </span>
              </div>

              <div v-if="showOwnedFeatures" class="v2-card__body">
                <div class="cp-label">{{ $t('profile.client.billing.owned_features') }}</div>
                <div class="cp-features">
                  <div v-for="feature in client.features" :key="'owned-' + feature.id" class="cp-feature">
                    <span class="cp-feature__icon"><font-awesome-icon icon="check"/></span>
                    <div class="cp-feature__text">
                      <div class="cp-feature__name">{{ feature.name }}</div>
                      <button v-if="client.subscription_status == 'subscription' && isSeatFeature(feature) && !client.onTrial"
                              type="button"
                              class="us-link"
                              @click="showFeatureConfirmSubscriptionModal(feature)">
                        {{ $t('profile.client.billing.update') }}
                      </button>
                      <span v-if="feature.type == 'plan' && !isSeatFeature(feature)" class="cp-feature__note">
                        {{ $t('profile.client.billing.from_plan') }}
                      </span>
                      <button v-if="feature.type == 'subscription' && !isSeatFeature(feature)"
                              type="button"
                              class="us-link pf-link--danger"
                              @click="showFeatureConfirmDeleteModal(feature)">
                        {{ $t('profile.client.billing.unsubscribe_label') }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="missingFeatures.length" class="v2-card__body cp-plan__missing">
                <div class="cp-label">{{ $t('profile.client.billing.missing_features') }}</div>
                <div class="cp-features">
                  <div v-for="feature in missingFeatures" :key="'missing-' + feature.id" class="cp-feature cp-feature--missing">
                    <span class="cp-feature__icon"><font-awesome-icon icon="plus"/></span>
                    <div class="cp-feature__text">
                      <div class="cp-feature__name">{{ feature.name }}</div>
                      <button v-if="client.subscription_status == 'subscription'"
                              type="button"
                              class="us-link"
                              @click="showFeatureConfirmSubscriptionModal(feature)">
                        {{ $t('profile.client.billing.read_more') }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Packages  -->
            <div class="card mt-3" v-if="client && client.plan && client.subscription_status != 'enterprise'">
              <div class="card-body">
                <h5>{{ $t('profile.client.billing.packages_title') }}</h5>

                <div class="row mt-2" style="max-width:1200px">

                  <div class="col-sm-12 col-md-6 col-lg-4" v-for="(plan, index) in plans">
                    <!-- First two cards -->
                    <div class="alert-feature card-shadow" :class="{'alert-feature-active': client.subscription_status == 'subscription' &&  plan.name == client.plan.name}">
                      <h5>
                        <font-awesome-icon style="color:#124A4E;" class="mr-2" icon="rocket" size="lg" v-if="index == 0" />
                        <font-awesome-icon style="color:#124A4E;" class="mr-2" icon="space-shuttle" size="lg" v-if="index == 1" />
                        {{ plan.name }}
                      </h5>
                      <div class="mt-3" style="min-height:51px;">
                        <span class="text-normal" v-if="index == 0">
                          {{ $t('profile.client.billing.article_30_description') }}
                        </span>
                        <span class="text-normal" v-if="index == 1">
                          {{ $t('profile.client.billing.privacy_description') }}
                        </span>
                      </div>
                      <div class="mt-3">
                        <span class="text-muted">
                          {{ $t('system.from') }}
                        </span>
                      </div>
                      <div class="mt-1">
                        <h5 class="mb-1">&euro; {{ plan.price }}</h5>
                        <span class="text-muted" v-if="client.subscription_status != 'enterprise'">
                          {{ $t('profile.client.billing.per_month') }}
                        </span>
                        <span class="text-muted" v-if="client.subscription_status == 'enterprise'">
                          {{ $t('system.from') }}
                        </span>
                      </div>
                      <div class="mt-3">
                        <span class="btn btn-disabled btn-block cursor-pointer" v-if="plan.name == client.plan.name && client.subscription_status == 'subscription'">
                          <span>
                            {{ $t('profile.client.billing.current_plan_button') }}
                          </span>
                        </span>
                        <span class="btn btn-success btn-block cursor-pointer" @click="showResume(plan)" v-else-if="plan.name == client.plan.name && client.subscription_status == 'subscription_canceled'">
                          <span>
                            {{ $t('profile.client.billing.resume_plan_button') }}
                          </span>
                        </span>
                        <span class="btn btn-success btn-block cursor-pointer" @click="showCartModal(plan)" v-else>
                          <span>
                            {{ $t('profile.client.billing.select_plan_button') }}
                          </span>
                        </span>
                      </div>
                      <div class="mt-1 text-center" v-if="plan.name == client.plan.name && client.subscription_status == 'subscription'" @click="showUnsubscribe()">
                        <span class="billing-unsubscribe-button">
                          {{ $t('profile.client.billing.unsubscribe_label') }}
                        </span>
                      </div>
                      <div class="mt-4 mb-1">
                        <span for="">{{ $t('profile.client.billing.includes') }}:</span>
                      </div>
                      <div class="mt-2" v-if="feature.module != 'admin'" v-for="feature in plan.features">
                        <font-awesome-icon class="text-success mr-2" icon="check" size="lg" />
                        <span class="text-normal">
                          {{ feature.name }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="col-sm-12 col-md-6 col-lg-4">
                    <!-- Third card -->
                    <div class="alert-feature card-shadow mr-3 float-left">
                      <h5>
                        <font-awesome-icon style="color:#124A4E;" class="mr-2" icon="starship" size="lg" />
                        {{ $t('profile.client.billing.enterprise_plan_title') }}
                      </h5>
                      <div class="mt-3" style="min-height:51px;">
                        <span class="text-normal">
                          {{ $t('profile.client.billing.enterprise_plan_description') }}
                        </span>
                      </div>
                      <div class="mt-3">
                        <span class="text-muted">
                          {{ $t('system.from') }}
                        </span>
                      </div>
                      <div class="mt-1">
                        <h5 class="mb-1">&euro; 999</h5>
                        <span class="text-muted">
                          {{ $t('profile.client.billing.per_month') }}
                        </span>
                      </div>
                      <div class="mt-3">
                        <span class="btn btn-success btn-block cursor-pointer" v-if="!checkAdmin(permissions.ROLE_SUPER_ADMIN)" @click="goToContactPage()">
                          {{ $t('profile.client.billing.contact_us_label') }}
                        </span>
                        <span class="btn btn-success btn-block cursor-pointer" v-if="checkAdmin(permissions.ROLE_SUPER_ADMIN)" @click="showConfirmEnterpriseModal(client)">
                          {{ $t('profile.client.billing.select_plan_button') }}
                        </span>
                      </div>
                      <div class="mt-4 mb-1">
                        <span for="">{{ $t('profile.client.billing.combine_package_info') }}</span>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>

        </div>
        <div v-if="billingPage === 'payments' && client.subscription_status != 'enterprise'" class="cp-legacy">
            <div class="card">
              <div class="card-body">
                <h5>
                  {{ $t('profile.client.billing.payments_title') }}
                </h5>
                <div>
                  <table class="table table-responsive-md table-list-secondary">
                    <thead>
                    <tr>
                      <th scope="col">
                        {{ $t('system.number') }}
                      </th>
                      <th scope="col">
                        {{ $t('system.date') }}
                      </th>
                      <th scope="col">
                        {{ $t('system.total') }}
                      </th>
                      <th scope="col">
                        {{ $t('system.status') }}
                      </th>
                      <th width="130"></th>
                    </tr>
                    </thead>
                    <tbody>
                      <tr v-for="invoice in invoices">
                        <td>
                          <a :href="invoice.invoice_pdf">{{ invoice.number }}</a>
                        </td>
                        <td>
                          {{ invoice.status_transitions.paid_at | timestamp}}
                        </td>
                        <td>
                          &euro; {{ invoice.total / 100 }}
                        </td>
                        <td>
                          {{ invoice.status | capitalize }}
                        </td>
                        <td>
                          <a class="btn btn-secondary-dark float-right" :href="invoice.invoice_pdf">
                            <font-awesome-icon icon="file-download"/>
                          </a>
                        </td>
                      </tr>
                      <tr v-if="!loadingInvoices && !invoices.length" class="text-center">
                        <td colspan="10">
                          <h5 class="mt-3">
                            {{ $t('system.no_results') }}
                          </h5>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
        </div>
        <ClientAiUsage v-if="billingPage === 'ai_usage' && canSeeUsage" :client="client"/>
      </div>
    </div>

    <!-- Preferences -->
    <div v-if="section === 'preferences'" class="cp-split">
      <nav class="cp-subnav">
        <button type="button" class="cp-subnav__item" :class="{ 'is-on': prefPage === 'notifications' }" qid="client-pref-notifications" @click="prefPage = 'notifications'">
          <font-awesome-icon icon="bell"/>{{ $t('client_page.preferences.notifications') }}
        </button>
        <button v-if="!client.parent && client.riskModule" type="button" class="cp-subnav__item" :class="{ 'is-on': prefPage === 'risk' }" qid="client-pref-risk" @click="prefPage = 'risk'">
          <font-awesome-icon icon="shield-alt"/>{{ $t('client_page.preferences.risk') }}
        </button>
        <button v-if="client.advancedReportingModule" type="button" class="cp-subnav__item" :class="{ 'is-on': prefPage === 'reporting' }" qid="client-pref-reporting" @click="prefPage = 'reporting'">
          <font-awesome-icon icon="chart-bar"/>{{ $t('client_page.preferences.reporting') }}
        </button>
        <template v-if="client.subscription_status == 'enterprise'">
          <div class="cp-subnav__heading">{{ $t('client_page.preferences.enterprise') }}</div>
          <button type="button" class="cp-subnav__item" :class="{ 'is-on': prefPage === 'access' }" qid="client-pref-access" @click="prefPage = 'access'">
            <font-awesome-icon icon="key"/>{{ $t('client_page.preferences.access') }}
          </button>
        </template>
      </nav>
      <div class="cp-split__main">
        <ClientEmailSettings v-if="prefPage === 'notifications' && client.id" :client="client" :can-edit="checkIfAdmin()"/>
        <div v-if="prefPage === 'risk' && !client.parent && client.riskModule" class="cp-stack" qid="client-risk-settings">
          <div class="v2-card">
            <div class="v2-card__head">
              <h3>{{ $t('profile.client.risk_settings_label') }}</h3>
            </div>
            <div class="v2-card__body">
              <div class="cp-label">{{ $t('profile.client.risk_type_label') }}</div>
              <div class="cp-modes cp-modes--two" role="radiogroup">
                <button v-for="option in riskTypeOptions"
                        :key="option.value"
                        type="button"
                        role="radio"
                        class="cp-mode"
                        :class="{ 'is-on': (selectedRiskType && selectedRiskType.value || client.risk_type) === option.value }"
                        :aria-checked="(selectedRiskType && selectedRiskType.value || client.risk_type) === option.value ? 'true' : 'false'"
                        :disabled="!checkIfAdmin()"
                        :qid="'client-risk-type-' + option.value"
                        @click="pickRiskType(option)">
                  <span class="cp-mode__title">{{ $t('profile.client.' + option.value) }}</span>
                  <span class="cp-mode__text">{{ $t('client_page.risk.text.' + option.value) }}</span>
                </button>
              </div>
              <div v-if="showRiskForm" class="cp-confirm-row">
                <div class="us-note us-note--warn">
                  <font-awesome-icon icon="exclamation-triangle"/>
                  <span>{{ $t('client_page.risk.change_note') }}</span>
                </div>
                <b-button variant="secondary" qid="cancel-button" @click="cancelRiskForm">
                  {{ $t('profile_page.edit.cancel') }}
                </b-button>
                <b-button variant="success" qid="submit-button" @click="formRiskTypeSubmit">
                  {{ $t('profile_page.edit.save') }}
                </b-button>
              </div>
            </div>
          </div>
        </div>
        <div v-if="prefPage === 'reporting' && client.advancedReportingModule" class="cp-stack" qid="client-reporting">
          <div class="v2-card">
            <div class="v2-card__head">
              <h3>{{ $t('profile.client.access_token') }}</h3>
              <button v-if="checkIfAdmin()"
                      type="button"
                      class="pf-btn pf-btn--secondary"
                      qid="client-token-generate"
                      @click="showRefreshClientModal(client)">
                <font-awesome-icon icon="redo-alt"/>
                {{ client.api_access_token ? $t('client_page.reporting.regenerate') : $t('system.generate_token') }}
              </button>
            </div>
            <div class="v2-card__body">
              <p class="cp-page-text cp-page-text--flush">{{ $t('client_page.reporting.token_text') }}</p>
              <div v-if="client.api_access_token" class="cp-token" qid="client-token">
                <font-awesome-icon icon="key" class="cp-token__icon"/>
                <code class="cp-token__value">{{ client.api_access_token }}</code>
                <button type="button" class="us-icon-btn" :title="$t('client_page.reporting.copy')" :aria-label="$t('client_page.reporting.copy')" @click="copyText(client.api_access_token)">
                  <font-awesome-icon icon="copy"/>
                </button>
                <button v-if="checkIfAdmin()"
                        type="button"
                        class="us-icon-btn us-icon-btn--danger"
                        :title="$t('client_page.reporting.delete_token')"
                        :aria-label="$t('client_page.reporting.delete_token')"
                        qid="clients-delete-button"
                        @click="showConfirmDeleteModal(client)">
                  <font-awesome-icon icon="trash-alt"/>
                </button>
              </div>
              <div v-else class="cp-empty">
                <font-awesome-icon icon="key"/>{{ $t('client_page.reporting.no_token') }}
              </div>
            </div>
          </div>

          <div class="v2-card">
            <div class="v2-card__head">
              <h3>{{ $t('profile.client.power_bi_configuration') }} <span class="v2-card__sub">· {{ tableItems.length }}</span></h3>
              <button v-if="checkIfAdmin()"
                      type="button"
                      class="pf-btn pf-btn--primary"
                      qid="question-create-button"
                      @click="showCreateReportModal()">
                <font-awesome-icon icon="plus"/>{{ $t('profile.client.report_create_button') }}
              </button>
            </div>
            <table class="us-table cp-usage-table">
              <thead>
                <tr>
                  <th class="cp-drag-col"></th>
                  <th>{{ $t('profile.client.table.name') }}</th>
                  <th>{{ $t('profile.client.table.ctid') }}</th>
                  <th>{{ $t('profile.client.table.report_id') }}</th>
                  <th>{{ $t('profile.client.table.page_name') }}</th>
                  <th class="cp-actions-col"></th>
                </tr>
              </thead>
              <draggable v-model="tableItems"
                         :force-fallback="true"
                         :disabled="!checkIfAdmin()"
                         handle=".cp-drag"
                         tag="tbody"
                         @start="drag=true"
                         @end="drag=false"
                         @change="postOrder">
                <tr v-for="object in tableItems" :key="object.id">
                  <td class="cp-drag-col"><span class="cp-drag" :title="$t('client_page.reporting.drag')"><font-awesome-icon icon="grip-vertical"/></span></td>
                  <td><b>{{ object.name }}</b></td>
                  <td class="cp-mono">{{ object.ctid }}</td>
                  <td class="cp-mono">{{ object.report_id }}</td>
                  <td>{{ object.page_name }}</td>
                  <td class="cp-actions-col">
                    <div v-if="checkIfAdmin()" class="cp-row-actions">
                      <button type="button" class="us-icon-btn" :aria-label="$t('client_page.reporting.edit_report')" qid="edit-v-modal" @click="showEditReportModal(object)">
                        <font-awesome-icon icon="pen"/>
                      </button>
                      <button type="button" class="us-icon-btn us-icon-btn--danger" :aria-label="$t('client_page.reporting.delete_report')" qid="delete-question-modal" @click="showReportConfirmDeleteModal(object)">
                        <font-awesome-icon icon="trash-alt"/>
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-if="!loading && !tableItems.length" key="empty" qid="no-results-row-rr">
                  <td colspan="6" class="cp-empty-cell">{{ $t('client_page.reporting.no_reports') }}</td>
                </tr>
              </draggable>
            </table>
          </div>
        </div>
        <div v-if="prefPage === 'access' && client.subscription_status == 'enterprise'" class="cp-stack" qid="client-access">
          <div class="v2-card">
            <div class="v2-card__head">
              <h3>{{ $t('profile.client.domain_access') }} <span class="v2-card__sub">· {{ domainTableItems.length }}</span></h3>
              <button v-if="checkIfAdmin()"
                      type="button"
                      class="pf-btn pf-btn--primary"
                      qid="question-create-button"
                      @click="showCreateDomainModal()">
                <font-awesome-icon icon="plus"/>{{ $t('profile.client.domain_create_button') }}
              </button>
            </div>
            <div class="v2-card__body">
              <p class="cp-page-text cp-page-text--flush">{{ $t('client_page.access.text') }}</p>
            </div>
            <table class="us-table cp-usage-table">
              <thead>
                <tr>
                  <th>{{ $t('profile.client.domain_label') }}</th>
                  <th>{{ $t('system.last_updated') }}</th>
                  <th class="cp-actions-col"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="object in domainTableItems" :key="object.id">
                  <td><div class="cp-who"><span class="cp-domain-icon"><font-awesome-icon icon="globe"/></span><b>{{ object.domain }}</b></div></td>
                  <td class="us-muted">{{ object.updated_at | formatDate }}</td>
                  <td class="cp-actions-col">
                    <div v-if="checkIfAdmin()" class="cp-row-actions">
                      <button type="button" class="us-icon-btn" :aria-label="$t('client_page.access.edit_domain')" qid="edit-v-modal" @click="showEditDomainModal(object)">
                        <font-awesome-icon icon="pen"/>
                      </button>
                      <button type="button" class="us-icon-btn us-icon-btn--danger" :aria-label="$t('client_page.access.delete_domain')" qid="delete-question-modal" @click="showDomainConfirmDeleteModal(object)">
                        <font-awesome-icon icon="trash-alt"/>
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-if="!loading && !domainTableItems.length" qid="no-results-row-rr">
                  <td colspan="3" class="cp-empty-cell">{{ $t('client_page.access.no_domains') }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit details: a side panel, like editing a user (US 9391) -->
    <b-modal ref="details-panel"
             modal-class="us-drawer"
             hide-header
             hide-footer>
      <div class="us-drawer__inner" qid="client-details-panel">
        <header class="us-drawer__head">
          <div class="us-drawer__who">
            <h2 class="us-drawer__name">{{ client.parent ? $t('client_page.details.edit_partition') : $t('client_page.details.edit_client') }}</h2>
            <div class="us-drawer__meta">{{ client.name }}</div>
          </div>
          <button type="button"
                  class="us-icon-btn us-drawer__close"
                  :aria-label="$t('profile_page.edit.cancel')"
                  @click="hideFormFunction">
            <font-awesome-icon icon="times"/>
          </button>
        </header>
        <div class="us-drawer__body">
          <b-alert v-if="clientForm.error && clientForm.error.length"
                   show
                   variant="danger"
                   qid="edit-user-error">{{ clientForm.error }}</b-alert>
          <section class="us-drawer__section">
            <h3 class="us-drawer__label">{{ $t('client_page.details.logo') }}</h3>
            <div class="pf-photo-row">
              <span class="cp-logo cp-logo--tiny">
                <img v-if="client.logo" :src="client.logo" alt="">
                <span v-else>{{ initials(client.name) }}</span>
              </span>
              <label class="pf-btn pf-btn--secondary" qid="client-upload-logo">
                <font-awesome-icon icon="cloud-upload-alt"/>
                {{ client.logo ? $t('client_page.details.change_logo') : $t('client_page.details.upload_logo') }}
                <input type="file"
                       accept="image/png,image/jpeg,image/gif"
                       class="d-none"
                       @change="onFileChange">
              </label>
              <button v-if="client.logo" type="button" class="us-link pf-link--danger" qid="client-remove-logo" @click="removePhoto">
                {{ $t('client_page.details.remove_logo') }}
              </button>
            </div>
            <div class="pf-hint">{{ $t('client_page.details.logo_hint') }}</div>
          </section>
          <section class="us-drawer__section">
            <label class="pf-field">
              <span class="pf-field__label">{{ $t('administration.clients.create.name_label') }}</span>
              <b-form-input v-model="clientForm.name" qid="create-user-form-name"/>
            </label>
            <div class="pf-row2">
              <label class="pf-field">
                <span class="pf-field__label">{{ $t('administration.clients.create.cvr_label') }}</span>
                <b-form-input v-model="clientForm.cvr" qid="create-client-form-cvr"/>
              </label>
              <label class="pf-field">
                <span class="pf-field__label">{{ $t('administration.partitions.create.reference_label') }}</span>
                <b-form-input v-model="clientForm.reference_number" qid="create-client-form-reference"/>
              </label>
            </div>
            <label class="pf-field">
              <span class="pf-field__label">{{ $t('administration.partitions.create.website_label') }}</span>
              <b-form-input v-model="clientForm.website" qid="create-client-form-website"/>
            </label>
          </section>
          <section class="us-drawer__section">
            <h3 class="us-drawer__label">{{ $t('profile.client.address_label') }}</h3>
            <label class="pf-field">
              <span class="pf-field__label">{{ $t('administration.clients.create.street_label') }}</span>
              <b-form-input v-model="clientForm.street" qid="create-client-form-street"/>
            </label>
            <div class="pf-row2">
              <label class="pf-field">
                <span class="pf-field__label">{{ $t('administration.clients.create.postcode_label') }}</span>
                <b-form-input v-model="clientForm.postcode" qid="create-client-form-postcode"/>
              </label>
              <label class="pf-field">
                <span class="pf-field__label">{{ $t('administration.clients.create.city_label') }}</span>
                <b-form-input v-model="clientForm.city" qid="create-clients-form-city"/>
              </label>
            </div>
            <label class="pf-field">
              <span class="pf-field__label">{{ $t('administration.clients.create.state_label') }}</span>
              <b-form-input v-model="clientForm.state" qid="create-clients-form-state"/>
            </label>
            <div class="pf-field">
              <span class="pf-field__label">{{ $t('administration.clients.create.country_label') }}</span>
              <multiselect v-model="selectedCountry"
                           :options="countries"
                           :multiple="false"
                           :taggable="false"
                           track-by="name"
                           :select-label="$t('system.dropdown_select_label')"
                           :selected-label="$t('system.dropdown_selected_label')"
                           :deselect-label="$t('system.dropdown_deselect_label')"
                           :placeholder="$t('administration.clients.create.country_label')"
                           label="name"
                           qid="create-tp-countries-select">
                <template slot="singleLabel" slot-scope="props">
                  <img v-bind:src="props.option.flag" height="13" class="align-baseline mr-1" />
                  <span class="ml-2">{{ props.option.name }}</span>
                </template>
                <template slot="option" slot-scope="props">
                  <img v-bind:src="props.option.flag" height="13" class="align-baseline mr-1" />
                  <span class="ml-2">{{ props.option.name }}</span>
                </template>
              </multiselect>
            </div>
          </section>
        </div>
        <footer class="us-drawer__foot">
          <span class="us-grow"></span>
          <b-button variant="secondary" qid="integrity-edit-cancel-button" @click="hideFormFunction">
            {{ $t('profile_page.edit.cancel') }}
          </b-button>
          <b-button variant="success"
                    :disabled="buttonDisabled"
                    qid="integrity-edit-submit-button"
                    @click="clientFormSubmit">
            <font-awesome-icon v-if="buttonDisabled" icon="spinner" class="fa-spin mr-1"/>
            {{ $t('profile_page.edit.save') }}
          </b-button>
        </footer>
      </div>
    </b-modal>

    <b-modal ref="enterprise-client" hide-footer>
      <div class="d-block text-center">
        <h4 qid="delete-client-title">
          {{ $t('administration.clients.enterprise.title') }}
        </h4>
      </div>
      <h5 class="text-center">
        {{ $t('administration.clients.enterprise.description') }} {{ itemToEnterprise.name }}
      </h5>
      <h5 class="text-center">
        {{ $t('administration.clients.enterprise.are_you_sure') }}
      </h5>
      <div class="text-center">
        <b-button class="mt-3"
                  variant="secondary"
                  @click="hideConfirmEnterpriseModal"
                  qid="delete-client-cancel-button"
        >
          {{ $t('administration.clients.enterprise.cancel') }}
        </b-button>
        <b-button @click="confirmEnterpriseClient(itemToEnterprise)"
                  class="mt-3 ml-3"
                  variant="success"
                  qid="delete-client-submit-button"
        >
          {{ $t('administration.clients.enterprise.submit') }}
        </b-button>
      </div>
    </b-modal>

    <!-- Refresh client token confirm modal template -->
    <b-modal ref="refresh-client" modal-class="v2-modal" hide-footer>
      <template #modal-header>
        <div class="us-modal-head">
          <div>
            <h5 class="mb-0">{{ $t('client_page.reporting.regenerate_title') }}</h5>
          </div>
          <button type="button" class="us-icon-btn" :aria-label="$t('profile_page.edit.cancel')" @click="hideRefreshClientModal">
            <font-awesome-icon icon="times"/>
          </button>
        </div>
      </template>
      <div class="cp-confirm">
        <span class="cp-confirm__icon cp-confirm__icon--teal"><font-awesome-icon icon="redo-alt"/></span>
        <div>
          <b v-if="itemToRefresh.name">{{ itemToRefresh.name }}</b>
          <p class="mb-0">{{ $t('client_page.reporting.regenerate_text') }}</p>
        </div>
      </div>
      <div class="us-modal-foot">
        <span class="us-grow"></span>
        <b-button variant="secondary" qid="delete-client-cancel-button" @click="hideRefreshClientModal">{{ $t('profile_page.edit.cancel') }}</b-button>
        <b-button variant="success" qid="delete-client-submit-button" @click="confirmRefreshClient(itemToRefresh)">{{ $t('client_page.reporting.regenerate') }}</b-button>
      </div>
    </b-modal>


    <!-- Client token confirm modal template -->
    <b-modal ref="client-token" modal-class="v2-modal" hide-footer>
      <template #modal-header>
        <div class="us-modal-head">
          <div>
            <h5 class="mb-0">{{ $t('administration.clients.token.title') }}</h5>
          </div>
          <button type="button" class="us-icon-btn" :aria-label="$t('profile_page.edit.cancel')" @click="hideClientTokenModal">
            <font-awesome-icon icon="times"/>
          </button>
        </div>
      </template>
      <div class="us-note us-note--warn">
        <font-awesome-icon icon="exclamation-triangle"/>
        <span>{{ $t('administration.clients.token.description') }}</span>
      </div>
      <div class="cp-token mt-3">
        <font-awesome-icon icon="key" class="cp-token__icon"/>
        <code class="cp-token__value">{{ selectedToken }}</code>
        <button type="button" class="us-icon-btn" :title="$t('client_page.reporting.copy')" :aria-label="$t('client_page.reporting.copy')" @click="copyText(selectedToken)">
          <font-awesome-icon icon="copy"/>
        </button>
      </div>
      <div class="us-modal-foot">
        <span class="us-grow"></span>
        <b-button variant="success" qid="delete-client-cancel-button" @click="hideClientTokenModal">{{ $t('system.close') }}</b-button>
      </div>
    </b-modal>


    <!-- Cart modal -->
    <b-modal ref="cart-modal" hide-footer hide-header :size="size" modal-class="cart-modal" @hide="hideCartModal">
      <div class="row" v-if="!client.country">
        <div class="col-sm-12">
          <div class="d-block text-center">
            <h4 qid="delete-client-title">
              {{ $t('profile.client.billing.country_modal_title') }}
            </h4>
          </div>
          <b-form qid="report-form">
            <div class="form-container">
              <b-form-group>
                <div class="row">
                  <div class="col-md-12">
                    <label qid="create-client-name-label">
                      {{ $t('administration.clients.create.country_label') }}
                      <span class="text-red">*</span>
                    </label>
                    <multiselect
                        v-model="selectedCountry"
                        :options="countries"
                        :multiple="false"
                        :taggable="false"
                        track-by="name"
                        :select-label="$t('system.dropdown_select_label')"
                        :selected-label="$t('system.dropdown_selected_label')"
                        :deselect-label="$t('system.dropdown_deselect_label')"
                        :placeholder="''"
                        label="name"
                        qid="create-tp-countries-select"
                    >
                      <template slot="singleLabel" slot-scope="props">
                        <img v-bind:src="props.option.flag" height="13" class="align-baseline mr-1" />
                        <span class="ml-2">
                          {{ props.option.name }}
                        </span>
                      </template>
                      <template slot="option" slot-scope="props">
                        <img v-bind:src="props.option.flag" height="13" class="align-baseline mr-1" />
                        <span class="ml-2">
                          {{ props.option.name }}
                        </span>
                      </template>
                    </multiselect>
                  </div>
                </div>
              </b-form-group>
            </div>
            <div class="text-right">
              <b-button class="mt-2"
                        variant="secondary"
                        @click="hideCartModal"
                        qid="integrity-edit-cancel-button"
              >
                {{ $t('activities.integrity_check.cancel') }}
              </b-button>
              <b-button class="mt-2 ml-1 mr-0"
                        variant="success"
                        @click="countryFormSubmit"
                        qid="integrity-edit-submit-button"
              >
                {{ $t('activities.integrity_check.submit') }}
              </b-button>
            </div>
          </b-form>
        </div>
      </div>
      <div class="row" v-if="client.country">
        <div class="col-sm-8 p-2">
          <div class="d-block text-left mt-4">
            <h4 qid="update-tp-title">
              {{ $t('profile.client.billing.modal_title') }}
            </h4>
          </div>
          <hr>
          <div class="mt-4">
            <div class="row">
              <div class="col-sm-4">
                <small class="text-muted uppercase">
                  {{ $t('profile.client.billing.plan_label') }}
                </small>
              </div>
              <div class="col-sm-3">
                <small class="text-muted uppercase">
                  {{ $t('profile.client.billing.quantity_label') }}
                </small>
              </div>
              <div class="col-sm-3">
                <small class="text-muted uppercase">
                  {{ $t('profile.client.billing.price_label') }}
                </small>
              </div>
              <div class="col-sm-2">
                <small class="text-muted uppercase">
                  {{ $t('profile.client.billing.total_label') }}
                </small>
              </div>
            </div>

            <!-- Selected plan -->

            <div class="row mt-3" v-if="selectedPlan && selectedPlan.id">
              <div class="col-sm-4 fs-1">
                {{ selectedPlan.name }}
              </div>
              <div class="col-sm-3 fs-1">

              </div>
              <div class="col-sm-3 fs-1">

              </div>
              <div class="col-sm-2 fs-1" v-if="client && client.plan">
                <span v-if="client.subscription_status == 'trial' || client.subscription_status == 'trial_expired' || client.subscription_status == 'subscription_ended' || client.subscription_status == 'subscription_canceled'">
                  &euro; {{ selectedPlan.price }}
                </span>
                <span v-if="(client.subscription_status != 'trial' && client.subscription_status != 'trial_expired' && client.subscription_status != 'subscription_ended'  && client.subscription_status != 'subscription_canceled') && selectedPlan.name == client.plan.name">
                  &euro; 0
                </span>
                <span v-if="(client.subscription_status != 'trial' && client.subscription_status != 'trial_expired' && client.subscription_status != 'subscription_ended'  && client.subscription_status != 'subscription_canceled') && selectedPlan.name != client.plan.name">
                  &euro; {{ selectedPlan.price }}
                </span>
              </div>
            </div>

            <!-- Included modules -->

            <div class="mt-4">
              <small class="text-muted uppercase">
                {{ $t('profile.client.billing.modules_label') }}
              </small>
            </div>

            <div class="row mt-3" v-if="selectedPlan && selectedPlan.id && feature.module != 'admin'" v-for="feature in selectedPlan.features">
              <div class="col-sm-4 fs-1">
                {{ feature.name }}
                <br>
                <div class="text-muted text-normal" style="font-size:10px;margin-top:-2px;">
                  {{ $t('profile.client.billing.from_plan_label') }}
                </div>
              </div>
              <div class="col-sm-3 fs-1">

              </div>
              <div class="col-sm-3 fs-1">

              </div>
              <div class="col-sm-2 fs-1">
                &euro; 0
              </div>
            </div>

            <!-- Selected features -->

            <div class="row mt-3" v-if="selectedPlan && selectedPlan.id" v-for="(selectedFeature, selectedIndex) in selectedFeatures">
              <div class="col-sm-4 fs-1">
                {{ selectedFeature.name }}
                <br>
                <div class="text-muted text-normal" style="font-size:10px;margin-top:-2px;">
                  <span @click="removeFeature(selectedIndex, selectedFeature)" class="cursor-pointer remove-label">
                    {{ $t('profile.client.billing.remove_label') }}
                  </span>
                </div>
              </div>
              <div class="col-sm-3 fs-1">

              </div>
              <div class="col-sm-3 fs-1">

              </div>
              <div class="col-sm-2 fs-1">
                &euro; {{ selectedFeature.price }}
              </div>
            </div>

          </div>
        </div>
        <div class="col-sm-4 p-2">
          <span @click="hideCartModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <div class="d-block text-left mt-4">
            <h4 qid="update-tp-title">
              {{ $t('profile.client.billing.order_summary') }}
            </h4>
          </div>
          <hr>
          <div class="mt-4">
            <div class="row">
              <div class="col-sm-8 uppercase fs-1">
                {{ $t('profile.client.billing.shopping_cart_label') }}
              </div>
              <div class="col-sm-4 text-right fs-1">
                &euro; {{ totalPrice }}
              </div>
            </div>
            <hr>
            <div class="row mt-4">
              <div class="col-sm-8 uppercase fs-1">
                {{ $t('profile.client.billing.total_cost_label') }}
              </div>
              <div class="col-sm-4 text-right fs-1">
                &euro; {{ totalPrice }}
              </div>
            </div>
            <div class="row mt-4">
              <div class="col-sm-12">
                <span class="btn btn-success btn-block cursor-pointer uppercase fs-1" @click="checkout()" :class="{'btn-disabled not-clickable': checkoutDisabled}">
                  {{ $t('profile.client.billing.checkout_label') }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>


      <!-- Hidden form element -->
      <form :action="'/api/clients/' + getClient.slug + '/checkout'" method="post" ref="form" style="visibility:hidden;height:1px;">
        <input type="text" id="admins" name="additional_admins" v-model="adminsAmount"><br>
        <input type="text" id="validators" name="additional_validators" v-model="validatorsAmount"><br>
        <input type="text" id="members" name="additional_members" v-model="membersAmount"><br>
        <input type="text" id="partitions" name="additional_partitions" v-model="partitionsAmount"><br>
        <input type="text" id="plan" name="plan" v-model="selectedPlan.id"><br>
        <input type="text" id="api_token" name="api_token" v-model="getToken"><br>
        <input type="text" id="token" name="token" v-model="getToken"><br>
        <input type="text" id="features" name="features" v-model="selectedFeaturesIds"><br>
        <input type="submit" value="Submit">
      </form>

    </b-modal>


    <!-- Add modules modal -->
    <b-modal ref="modules-modal" modal-class="task-modal mapping-modal" @hide="hideModulesModal">
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideModulesModal" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-1 text-center">
            {{ $t('profile.client.billing.modules_modal_title') }}
          </h5>
        </div>
      </template>

      <div class="row">
        <div class="col-sm-6" v-for="(feature, index) in modalFeatures">
          <div class="section-container mt-2"
               :class="{'section-container-selected': feature.from_plan || feature.selected,
                        'section-container-unavailable': (feature.module == 'gdpr_advanced' && !isAvailable(feature)) || (feature.module == 'contract_advanced' && !isAvailable(feature)) || (feature.module == 'assessment' && !isAvailable(feature))}">
            <span style="height:40px;" class="float-left mr-2">
              <span @click="selectFeature(index, feature)" :class="{'not-clickable': feature.from_plan || ((feature.module == 'gdpr_advanced' && !isAvailable(feature)) || (feature.module == 'contract_advanced' && !isAvailable(feature)) || (feature.module == 'assessment' && !isAvailable(feature)))}" class="cursor-pointer">
                <font-awesome-icon :icon="['far', 'square']" style="color:#BCDAD4;margin-bottom:1px;" v-if="!feature.selected"/>
                <font-awesome-icon icon="check-square" style="color:#2DBA9B;margin-bottom:1px;" v-if="feature.selected && !feature.from_plan"/>
                <font-awesome-icon icon="check-square" style="color:#BCDAD4;margin-bottom:1px;" v-if="feature.selected && feature.from_plan"/>
              </span>
            </span>
            <span>
              <span class="text-normal">{{ feature.name }}</span>
              <br>
              <span class="text-bold" v-if="!feature.from_plan">&euro; {{ feature.price }}</span>
              <span class="text-muted" v-if="feature.from_plan">
                {{ $t('profile.client.billing.from_plan_label') }}
              </span>
            </span>
          </div>
        </div>
      </div>


      <template #modal-footer="{ close }">
        <div class="" style="width: 100%;">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">

              </div>
              <div class="col-sm-6">
                <div class="text-right">
                  <b-button class=""
                            variant="secondary"
                            @click="hideModulesModal"
                            qid="create-ac-cancel-button"
                  >
                    {{ $t('activities.system.create.cancel') }}
                  </b-button>
                  <b-button type="submit"
                            class="ml-3"
                            variant="success"
                            @click="addFeatures()"
                            :disabled="buttonDisabled"
                            qid="create-ac-submit-button"
                  >
                    <span v-if="buttonDisabled" class="mr-1">
                      <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                    </span>
                    {{ $t('profile.client.billing.add_selected_features') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

    </b-modal>


    <!-- Edit report modal -->
    <b-modal ref="edit-report" modal-class="v2-modal" hide-footer>
      <template #modal-header>
        <div class="us-modal-head">
          <div>
            <h5 class="mb-0">{{ $t('profile.client.edit_report_title') }}</h5>
          </div>
          <button type="button" class="us-icon-btn" :aria-label="$t('profile_page.edit.cancel')" @click="hideEditReportModal">
            <font-awesome-icon icon="times"/>
          </button>
        </div>
      </template>
      <b-alert v-if="reportForm.error && reportForm.error.length" show variant="danger" qid="edit-user-error">{{ reportForm.error }}</b-alert>
      <b-form class="cp-form" qid="report-form" @submit="reportEditFormSubmit">
        <label class="pf-field">
          <span class="pf-field__label">{{ $t('profile.client.report_name_label') }} <span class="text-red">*</span></span>
          <b-form-input v-model="reportForm.name" required qid="create-user-form-name"/>
        </label>
        <div class="pf-row2">
          <label class="pf-field">
            <span class="pf-field__label">{{ $t('profile.client.report_ctid_label') }} <span class="text-red">*</span></span>
            <b-form-input v-model="reportForm.ctid" required class="cp-mono" qid="create-client-form-cvr"/>
          </label>
          <label class="pf-field">
            <span class="pf-field__label">{{ $t('profile.client.report_id_label') }} <span class="text-red">*</span></span>
            <b-form-input v-model="reportForm.reportId" required class="cp-mono" qid="create-client-form-street"/>
          </label>
        </div>
        <label class="pf-field">
          <span class="pf-field__label">{{ $t('profile.client.report_page_name_label') }}</span>
          <b-form-input v-model="reportForm.pageName" qid="create-clients-form-city"/>
          <span class="pf-hint">{{ $t('client_page.reporting.page_name_hint') }}</span>
        </label>
        <div class="us-modal-foot">
          <span class="us-grow"></span>
          <b-button variant="secondary" qid="edit-profile-cancel-button" @click="hideEditReportModal">{{ $t('profile_page.edit.cancel') }}</b-button>
          <b-button type="submit" variant="success" :disabled="buttonDisabled" qid="edit-profile-submit-button">
            <font-awesome-icon v-if="buttonDisabled" icon="spinner" class="fa-spin mr-1"/>
            {{ $t('profile_page.edit.save') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>


    <!-- Create report modal -->
    <b-modal ref="create-report" modal-class="v2-modal" hide-footer>
      <template #modal-header>
        <div class="us-modal-head">
          <div>
            <h5 class="mb-0">{{ $t('client_page.reporting.add_title') }}</h5>
          </div>
          <button type="button" class="us-icon-btn" :aria-label="$t('profile_page.edit.cancel')" @click="hideCreateReportModal">
            <font-awesome-icon icon="times"/>
          </button>
        </div>
      </template>
      <b-alert v-if="reportForm.error && reportForm.error.length" show variant="danger" qid="edit-user-error">{{ reportForm.error }}</b-alert>
      <b-form class="cp-form" qid="report-form" @submit="reportFormSubmit">
        <label class="pf-field">
          <span class="pf-field__label">{{ $t('profile.client.report_name_label') }} <span class="text-red">*</span></span>
          <b-form-input v-model="reportForm.name" required qid="create-user-form-name"/>
        </label>
        <div class="pf-row2">
          <label class="pf-field">
            <span class="pf-field__label">{{ $t('profile.client.report_ctid_label') }} <span class="text-red">*</span></span>
            <b-form-input v-model="reportForm.ctid" required class="cp-mono" qid="create-client-form-cvr"/>
          </label>
          <label class="pf-field">
            <span class="pf-field__label">{{ $t('profile.client.report_id_label') }} <span class="text-red">*</span></span>
            <b-form-input v-model="reportForm.reportId" required class="cp-mono" qid="create-client-form-street"/>
          </label>
        </div>
        <label class="pf-field">
          <span class="pf-field__label">{{ $t('profile.client.report_page_name_label') }}</span>
          <b-form-input v-model="reportForm.pageName" qid="create-clients-form-city"/>
          <span class="pf-hint">{{ $t('client_page.reporting.page_name_hint') }}</span>
        </label>
        <div class="us-modal-foot">
          <span class="us-grow"></span>
          <b-button variant="secondary" qid="edit-profile-cancel-button" @click="hideCreateReportModal">{{ $t('profile_page.edit.cancel') }}</b-button>
          <b-button type="submit" variant="success" :disabled="buttonDisabled" qid="edit-profile-submit-button">
            <font-awesome-icon v-if="buttonDisabled" icon="spinner" class="fa-spin mr-1"/>
            {{ $t('profile_page.edit.save') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>


    <!-- Edit domain modal -->
    <b-modal ref="edit-domain" modal-class="v2-modal" hide-footer>
      <template #modal-header>
        <div class="us-modal-head">
          <div>
            <h5 class="mb-0">{{ $t('profile.client.edit_domain_title') }}</h5>
          </div>
          <button type="button" class="us-icon-btn" :aria-label="$t('profile_page.edit.cancel')" @click="hideEditDomainModal">
            <font-awesome-icon icon="times"/>
          </button>
        </div>
      </template>
      <b-alert v-if="domainForm.error && domainForm.error.length" show variant="danger" qid="edit-user-error">{{ domainForm.error }}</b-alert>
      <b-form class="cp-form" qid="domain-form" @submit="domainEditFormSubmit">
        <label class="pf-field">
          <span class="pf-field__label">{{ $t('profile.client.domain_label') }} <span class="text-red">*</span></span>
          <b-form-input v-model="domainForm.name" required :placeholder="$t('client_page.access.domain_placeholder')" qid="create-user-form-name"/>
          <span class="pf-hint">{{ $t('client_page.access.domain_hint') }}</span>
        </label>
        <div class="us-modal-foot">
          <span class="us-grow"></span>
          <b-button variant="secondary" qid="edit-profile-cancel-button" @click="hideEditDomainModal">{{ $t('profile_page.edit.cancel') }}</b-button>
          <b-button type="submit" variant="success" :disabled="buttonDisabled" qid="edit-profile-submit-button">
            <font-awesome-icon v-if="buttonDisabled" icon="spinner" class="fa-spin mr-1"/>
            {{ $t('profile_page.edit.save') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>


    <!-- Create domain modal -->
    <b-modal ref="create-domain" modal-class="v2-modal" hide-footer>
      <template #modal-header>
        <div class="us-modal-head">
          <div>
            <h5 class="mb-0">{{ $t('client_page.access.add_title') }}</h5>
          </div>
          <button type="button" class="us-icon-btn" :aria-label="$t('profile_page.edit.cancel')" @click="hideCreateDomainModal">
            <font-awesome-icon icon="times"/>
          </button>
        </div>
      </template>
      <b-alert v-if="domainForm.error && domainForm.error.length" show variant="danger" qid="edit-user-error">{{ domainForm.error }}</b-alert>
      <b-form class="cp-form" qid="domain-form" @submit="domainFormSubmit">
        <label class="pf-field">
          <span class="pf-field__label">{{ $t('profile.client.domain_label') }} <span class="text-red">*</span></span>
          <b-form-input v-model="domainForm.name" required :placeholder="$t('client_page.access.domain_placeholder')" qid="create-user-form-name"/>
          <span class="pf-hint">{{ $t('client_page.access.domain_hint') }}</span>
        </label>
        <div class="us-modal-foot">
          <span class="us-grow"></span>
          <b-button variant="secondary" qid="edit-profile-cancel-button" @click="hideCreateDomainModal">{{ $t('profile_page.edit.cancel') }}</b-button>
          <b-button type="submit" variant="success" :disabled="buttonDisabled" qid="edit-profile-submit-button">
            <font-awesome-icon v-if="buttonDisabled" icon="spinner" class="fa-spin mr-1"/>
            {{ $t('profile_page.edit.save') }}
          </b-button>
        </div>
      </b-form>
    </b-modal>



    <!-- Delete client confirm modal template -->
    <b-modal ref="delete-client" modal-class="v2-modal" hide-footer>
      <template #modal-header>
        <div class="us-modal-head">
          <div>
            <h5 class="mb-0">{{ $t('client_page.reporting.delete_token_title') }}</h5>
          </div>
          <button type="button" class="us-icon-btn" :aria-label="$t('profile_page.edit.cancel')" @click="hideConfirmDeleteModal">
            <font-awesome-icon icon="times"/>
          </button>
        </div>
      </template>
      <div class="cp-confirm">
        <span class="cp-confirm__icon cp-confirm__icon--red"><font-awesome-icon icon="trash-alt"/></span>
        <div>
          <b v-if="itemToDelete.name">{{ itemToDelete.name }}</b>
          <p class="mb-0">{{ $t('client_page.reporting.delete_token_text') }}</p>
        </div>
      </div>
      <div class="us-modal-foot">
        <span class="us-grow"></span>
        <b-button variant="secondary" qid="delete-client-cancel-button" @click="hideConfirmDeleteModal">{{ $t('profile_page.edit.cancel') }}</b-button>
        <b-button variant="danger" qid="delete-client-submit-button" @click="confirmDeleteClient(itemToDelete)">{{ $t('client_page.reporting.delete_token') }}</b-button>
      </div>
    </b-modal>


    <!-- Delete domain modal template -->
    <b-modal ref="delete-domain" modal-class="v2-modal" hide-footer>
      <template #modal-header>
        <div class="us-modal-head">
          <div>
            <h5 class="mb-0">{{ $t('client_page.access.delete_title') }}</h5>
          </div>
          <button type="button" class="us-icon-btn" :aria-label="$t('profile_page.edit.cancel')" @click="hideDomainConfirmDeleteModal">
            <font-awesome-icon icon="times"/>
          </button>
        </div>
      </template>
      <div class="cp-confirm">
        <span class="cp-confirm__icon cp-confirm__icon--red"><font-awesome-icon icon="trash-alt"/></span>
        <div>
          <b v-if="itemToDelete.domain">{{ itemToDelete.domain }}</b>
          <p class="mb-0">{{ $t('client_page.access.delete_text') }}</p>
        </div>
      </div>
      <div class="us-modal-foot">
        <span class="us-grow"></span>
        <b-button variant="secondary" qid="delete-client-cancel-button" @click="hideDomainConfirmDeleteModal">{{ $t('profile_page.edit.cancel') }}</b-button>
        <b-button variant="danger" qid="delete-client-submit-button" @click="confirmDeleteDomain(itemToDelete)">{{ $t('client_page.access.delete_domain') }}</b-button>
      </div>
    </b-modal>


    <!-- Delete report modal template -->
    <b-modal ref="delete-report" modal-class="v2-modal" hide-footer>
      <template #modal-header>
        <div class="us-modal-head">
          <div>
            <h5 class="mb-0">{{ $t('client_page.reporting.delete_title') }}</h5>
          </div>
          <button type="button" class="us-icon-btn" :aria-label="$t('profile_page.edit.cancel')" @click="hideReportConfirmDeleteModal">
            <font-awesome-icon icon="times"/>
          </button>
        </div>
      </template>
      <div class="cp-confirm">
        <span class="cp-confirm__icon cp-confirm__icon--red"><font-awesome-icon icon="trash-alt"/></span>
        <div>
          <b v-if="itemToDelete.name">{{ itemToDelete.name }}</b>
          <p class="mb-0">{{ $t('client_page.reporting.delete_text') }}</p>
        </div>
      </div>
      <div class="us-modal-foot">
        <span class="us-grow"></span>
        <b-button variant="secondary" qid="delete-client-cancel-button" @click="hideReportConfirmDeleteModal">{{ $t('profile_page.edit.cancel') }}</b-button>
        <b-button variant="danger" qid="delete-client-submit-button" @click="confirmDeleteReport(itemToDelete)">{{ $t('client_page.reporting.delete_report') }}</b-button>
      </div>
    </b-modal>

    <!-- Delete subscription -->
    <b-modal ref="delete-subscription" hide-footer>
      <div>
        <div class="d-block text-center">
          <h4 qid="delete-report-title">
            {{ $t('profile.client.billing.unsubscribe_title') }}?
          </h4>
        </div>
        <hr />
        <p class="text-left mt-3">
          {{ $t('profile.client.billing.unsubscribe_description') }}
        </p>
        <p class="text-left">
          {{ $t('profile.client.billing.unsubscribe_question') }}
        </p>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideUnsubscribe()"
                    qid="delete-question-cancel-button"
          >
            {{ $t('maintenance.questions.delete.cancel') }}
          </b-button>
          <b-button @click="unsubscribeFunction()"
                    class="mt-3 ml-3"
                    variant="danger"
                    qid="delete-question-submit-button"
          >
            {{ $t('profile.client.billing.unsubscribe_title') }}
          </b-button>
        </div>
      </div>
    </b-modal>


    <!-- Unsubscribe success modal -->
    <b-modal ref="delete-subscription-success" hide-footer>
      <div class="text-center">
        <font-awesome-icon icon="heart-broken" size="10x" class="color-danger"/>
        <h4 class="mt-3">
          {{ $t('profile.client.billing.unsubscribe_success_title') }}
        </h4>
        <p class="text-left mt-3 text-muted">
          {{ $t('profile.client.billing.unsubscribe_success_description') }}
        </p>
        <div>
          <b-button @click="continueDeleteSubscription()"
                    class="mt-3 ml-3"
                    variant="success"
                    qid="delete-question-submit-button"
          >
            {{ $t('profile.client.billing.unsubscribe_success_continue') }}
          </b-button>
        </div>

      </div>
    </b-modal>


    <!-- Delete feature modal -->
    <b-modal ref="delete-feature" hide-footer>
      <div>
        <div class="d-block text-center">
          <h4 qid="delete-report-title">
            {{ $t('profile.client.billing.delete_feature_title') }}
          </h4>
        </div>
        <hr />
        <p class="text-left mt-3">
          {{ $t('profile.client.billing.delete_feature_first_description') }}
          <span v-if="featureToDelete && featureToDelete.name" class="text-success">
            {{ featureToDelete.name }}
          </span>
          <span>
            {{ $t('profile.client.billing.delete_feature_module_label') }}.
          </span>
          {{ $t('profile.client.billing.delete_feature_second_description') }}
        </p>
        <p class="text-left">
          {{ $t('profile.client.billing.delete_feature_question') }}
        </p>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideFeatureConfirmDeleteModal"
                    qid="delete-question-cancel-button"
          >
            {{ $t('maintenance.questions.delete.cancel') }}
          </b-button>
          <b-button @click="confirmDeleteFeature(featureToDelete)"
                    class="mt-3 ml-3"
                    variant="danger"
                    qid="delete-question-submit-button"
          >
            {{ $t('profile.client.billing.unsubscribe_title') }}
          </b-button>
        </div>
      </div>
    </b-modal>


    <!-- Resume subscription modal-->
    <b-modal ref="resume" hide-footer>
      <div>
        <div class="d-block text-center">
          <h4 qid="delete-report-title">
            {{ $t('profile.client.billing.resume_title') }}
          </h4>
        </div>
        <hr />
        <p class="text-left mt-3">
          {{ $t('profile.client.billing.resume_description') }}
        </p>
        <p class="text-left">
          {{ $t('profile.client.billing.resume_question') }}
        </p>
        <div class="text-right">
          <b-button class="mt-3"
                    variant="secondary"
                    @click="hideResume"
                    qid="delete-question-cancel-button"
          >
            {{ $t('maintenance.questions.delete.cancel') }}
          </b-button>
          <b-button @click="confirmResume(selectedPlan)"
                    class="mt-3 ml-3"
                    variant="danger"
                    qid="delete-question-submit-button"
          >
            {{ $t('profile.client.billing.resume_button') }}
          </b-button>
        </div>
      </div>
    </b-modal>
    <b-modal ref="resume-success" hide-footer>
      <div class="text-center">
        <font-awesome-icon icon="rocket" size="10x" class="color-success"/>
        <h4 class="mt-3">{{ $t('profile.client.billing.resume_success_title') }}</h4>
        <p class="text-left mt-3 text-muted">
          {{ $t('profile.client.billing.resume_success_description', { name: selectedPlan.name}) }}
        </p>
        <div>
          <b-button @click="continueResume(selectedPlan)"
                    class="mt-3 ml-3"
                    variant="success"
                    qid="delete-question-submit-button"
          >
            {{ $t('profile.client.billing.resume_success_continue') }}
          </b-button>
        </div>

      </div>
    </b-modal>


    <!-- delete feature success modal -->
    <b-modal ref="delete-feature-success" hide-footer>
      <div class="text-center">
        <font-awesome-icon icon="puzzle-piece" size="10x" class="color-danger"/>
        <h4 class="mt-3">
          {{ $t('profile.client.billing.delete_feature_success_title') }}
        </h4>
        <p class="text-left mt-3 text-muted">
          {{ $t('profile.client.billing.delete_feature_success_first_description') }}
          <span v-if="featureToDelete && featureToDelete.name" class="text-danger text-normal">
            {{ featureToDelete.name }}
          </span>
          <span>
            {{ $t('profile.client.billing.delete_feature_module_label') }}.
          </span>
          {{ $t('profile.client.billing.delete_feature_success_second_description') }}
        </p>
        <div>
          <b-button @click="continueDeleteFeature(featureToDelete)"
                    class="mt-3 ml-3"
                    variant="success"
                    qid="delete-question-submit-button"
          >
            {{ $t('profile.client.billing.unsubscribe_success_continue') }}
          </b-button>
        </div>

      </div>
    </b-modal>


    <!-- Add feature success modal -->
    <b-modal ref="subscription-feature-success" hide-footer>
      <div class="text-center">
        <font-awesome-icon icon="puzzle-piece" size="10x" class="color-success"/>
        <h4 class="mt-3" v-if="featureToSubscription.module != 'admin' && featureToSubscription.module != 'validator' && featureToSubscription.module != 'member' && featureToSubscription.module != 'partition'">
          {{ $t('profile.client.billing.add_feature_title') }}
        </h4>
        <h4 class="mt-3" v-if="featureToSubscription.module == 'admin' || featureToSubscription.module == 'validator' || featureToSubscription.module == 'member' || featureToSubscription.module == 'partition'">
          {{ $t('profile.client.billing.update_feature_title') }}
        </h4>
        <p class="text-left mt-3 text-muted" v-if="featureToSubscription.module != 'admin' && featureToSubscription.module != 'validator' && featureToSubscription.module != 'member' && featureToSubscription.module != 'partition'">
          {{ $t('profile.client.billing.add_feature_first_description') }}
          <span v-if="featureToSubscription && featureToSubscription.name" class="text-success text-normal">
            {{ featureToSubscription.name }}
          </span>
          <span>
            {{ $t('profile.client.billing.delete_feature_module_label') }}.
          </span>
        </p>

        <p class="text-left mt-3 text-muted" v-if="featureToSubscription.module == 'admin' || featureToSubscription.module == 'validator' || featureToSubscription.module == 'member' || featureToSubscription.module == 'partition'">
          {{ $t('profile.client.billing.quantity_feature_description') }}
          <span v-if="featureToSubscription && featureToSubscription.name" class="text-success text-normal">
            {{ featureToSubscription.name }}
          </span>
          <span>
            {{ $t('profile.client.billing.delete_feature_module_label') }}.
          </span>
        </p>
        <div>
          <b-button @click="continueSubscriptionFeature(featureToSubscription)"
                    class="mt-3 ml-3"
                    variant="success"
                    qid="delete-question-submit-button"
          >
            {{ $t('profile.client.billing.unsubscribe_success_continue') }}
          </b-button>
        </div>

      </div>
    </b-modal>


    <!-- Checkout success modal -->
    <b-modal ref="checkout-success" hide-footer>
      <div class="text-center">
        <font-awesome-icon icon="rocket" size="10x" class="color-success"/>
        <h4 class="mt-3">
          {{ $t('profile.client.billing.checkout_success_title') }}
        </h4>
        <p class="mt-3 text-muted">
          {{ $t('profile.client.billing.checkout_success_description') }}
        </p>
        <div>
          <b-button @click="hideCheckoutSuccessModal()"
                    class="mt-3 ml-3 uppercase"
                    variant="success"
                    qid="delete-question-submit-button"
          >
            {{ $t('profile.client.billing.unsubscribe_success_continue') }}
          </b-button>
        </div>

      </div>
    </b-modal>


    <!-- Subscription to feature -->
    <b-modal size="xl" ref="subscription-feature" hide-footer>
      <div class="d-block text-left">
        <div class="row">
          <div class="col-md-7">
            <h4 qid="delete-report-title">
              {{ $t('profile.client.billing.shopping_cart') }}
            </h4>
            <hr />
          </div>
          <div class="col-md-4 offset-md-1">
            <h4 qid="delete-report-title">
              {{ $t('profile.client.billing.order_summary') }}
            </h4>
            <hr />
          </div>
        </div>

        <div class="row">
          <div class="col-md-7">
            <div class="text-left mt-3">
              {{ $t('features.'+featureToSubscription.module) }}
            </div>

            <div class="row mt-4" v-if="client && client.plan && client.plan == 'Enterprise' || (featureToSubscription.module != 'admin' && featureToSubscription.module != 'validator' && featureToSubscription.module != 'member' && featureToSubscription.module != 'partition')">
              <div class="col-md-9">
                <div class="text-muted">{{ $t('profile.client.billing.feature_name') }}</div>
                <h5 class="text-lg-left mt-2">{{ featureToSubscription.name }}</h5>
              </div>
              <div class="col-md-3" v-if="client.plan != 'Enterprise'">
                <div class="text-muted text-right">{{ $t('profile.client.billing.total') }}</div>
                <h5 class="text-lg-right mt-2">&euro; {{ featureToSubscription.price }}</h5>
              </div>
            </div>

            <div class="row mt-4" v-if="client && client.plan && client.plan != 'Enterprise' && featureToSubscription.module == 'admin'">
              <div class="col-md-3">
                <div class="text-muted">{{ $t('profile.client.billing.feature_name') }}</div>
                <h5 class="text-lg-left mt-2">{{ featureToSubscription.name }}</h5>
              </div>
              <div class="col-md-3">
                <div class="text-muted text-center">{{ $t('profile.client.billing.quantity') }}</div>
                <div class="text-lg-center mt-2">
                  <font-awesome-icon icon="minus" class="cursor-pointer " @click="decreaseAdmins"/>
                  <span class="cart-number-input">
                  {{ adminsAmount }}
                  </span>
                  <font-awesome-icon icon="plus" class="cursor-pointer" @click="increaseAdmins"/>
                </div>
              </div>
              <div class="col-md-3">
                <div class="text-muted text-right">{{ $t('profile.client.billing.price') }}</div>
                <h5 class="text-lg-right mt-2">&euro; {{ adminFeature.price }}</h5>
              </div>
              <div class="col-md-3">
                <div class="text-muted text-right">{{ $t('profile.client.billing.total') }}</div>
                <h5 class="text-lg-right mt-2">&euro; {{ (adminsAmount - client.admin_limit) * adminFeature.price }}</h5>
              </div>
            </div>

            <div class="row mt-4" v-if="client && client.plan && client.plan != 'Enterprise' && featureToSubscription.module == 'partition'">
              <div class="col-md-3">
                <div class="text-muted">{{ $t('profile.client.billing.feature_name') }}</div>
                <h5 class="text-lg-left mt-2">{{ featureToSubscription.name }}</h5>
              </div>
              <div class="col-md-3">
                <div class="text-muted text-center">{{ $t('profile.client.billing.quantity') }}</div>
                <div class="text-lg-center mt-2">
                  <font-awesome-icon icon="minus" class="cursor-pointer " @click="decreasePartitions"/>
                  <span class="cart-number-input">
                  {{ partitionsAmount }}
                  </span>
                  <font-awesome-icon icon="plus" class="cursor-pointer" @click="increasePartitions"/>
                </div>
              </div>
              <div class="col-md-3">
                <div class="text-muted text-right">{{ $t('profile.client.billing.price') }}</div>
                <h5 class="text-lg-right mt-2">&euro; {{ partitionFeature.price }}</h5>
              </div>
              <div class="col-md-3">
                <div class="text-muted text-right">{{ $t('profile.client.billing.total') }}</div>
                <h5 class="text-lg-right mt-2">&euro; {{ (partitionsAmount - client.partition_limit) * partitionFeature.price }}</h5>
              </div>
            </div>

            <div class="row mt-4" v-if="client && client.plan && client.plan != 'Enterprise' && featureToSubscription.module == 'validator'">
              <div class="col-md-3">
                <div class="text-muted">{{ $t('profile.client.billing.feature_name') }}</div>
                <h5 class="text-lg-left mt-2">{{ featureToSubscription.name }}</h5>
              </div>
              <div class="col-md-3">
                <div class="text-muted text-center">{{ $t('profile.client.billing.quantity') }}</div>
                <div class="text-lg-center mt-2">
                  <font-awesome-icon icon="minus" class="cursor-pointer " @click="decreaseValidators"/>
                  <span class="cart-number-input">
                  {{ validatorsAmount }}
                  </span>
                  <font-awesome-icon icon="plus" class="cursor-pointer" @click="increaseValidators"/>
                </div>
              </div>
              <div class="col-md-3">
                <div class="text-muted text-right">{{ $t('profile.client.billing.price') }}</div>
                <h5 class="text-lg-right mt-2">&euro; {{ validatorFeature.price }}</h5>
              </div>
              <div class="col-md-3">
                <div class="text-muted text-right">{{ $t('profile.client.billing.total') }}</div>
                <h5 class="text-lg-right mt-2">&euro; {{ (validatorsAmount - client.validator_limit) * validatorFeature.price }}</h5>
              </div>
            </div>

            <div class="row mt-4" v-if="client && client.plan && client.plan != 'Enterprise' && featureToSubscription.module == 'member'">
              <div class="col-md-3">
                <div class="text-muted">{{ $t('profile.client.billing.feature_name') }}</div>
                <h5 class="text-lg-left mt-2">{{ featureToSubscription.name }}</h5>
              </div>
              <div class="col-md-3">
                <div class="text-muted text-center">{{ $t('profile.client.billing.quantity') }}</div>
                <div class="text-lg-center mt-2">
                  <font-awesome-icon icon="minus" class="cursor-pointer " @click="decreaseMembers"/>
                  <span class="cart-number-input">
                  {{ membersAmount }}
                  </span>
                  <font-awesome-icon icon="plus" class="cursor-pointer" @click="increaseMembers"/>
                </div>
              </div>
              <div class="col-md-3">
                <div class="text-muted text-right">{{ $t('profile.client.billing.price') }}</div>
                <h5 class="text-lg-right mt-2">&euro; {{ memberFeature.price }}</h5>
              </div>
              <div class="col-md-3">
                <div class="text-muted text-right">{{ $t('profile.client.billing.total') }}</div>
                <h5 class="text-lg-right mt-2">&euro; {{ (membersAmount - client.member_limit) * memberFeature.price }}</h5>
              </div>
            </div>

            <div class="row" v-if="userMessage">
              <div class="col-md-12">
                <span class="text-danger">{{userMessage}}</span>
              </div>
            </div>
          </div>

          <div class="col-md-4 offset-md-1" v-if="client && client.plan">
            <div class="row h6" v-if="client.plan != 'Enterprise'">
              <div class="col-md-9">{{ $t('profile.client.billing.existing_monthly_price') }}</div>
              <div class="col-md-3">&euro; {{ client.subscription_total }}</div>
            </div>
            <div class="row h6" v-if="client.plan != 'Enterprise'">
              <div class="col-md-9">{{ $t('profile.client.billing.added_monthly_price') }}</div>
              <div class="col-md-3" v-if="featureToSubscription.module != 'admin' && featureToSubscription.module != 'validator' && featureToSubscription.module != 'member' && featureToSubscription.module != 'partition'">&euro; {{ featureToSubscription.price }}</div>
              <div class="col-md-3" v-if="featureToSubscription.module == 'admin'">&euro; {{ (adminsAmount - client.admin_limit) * adminFeature.price }}</div>
              <div class="col-md-3" v-if="featureToSubscription.module == 'member'">&euro; {{ (membersAmount - client.member_limit) * memberFeature.price }}</div>
              <div class="col-md-3" v-if="featureToSubscription.module == 'validator'">&euro; {{ (validatorsAmount - client.validator_limit) * validatorFeature.price }}</div>
              <div class="col-md-3" v-if="featureToSubscription.module == 'partition'">&euro; {{ (partitionsAmount - client.partition_limit) * partitionFeature.price }}</div>
            </div>
            <hr v-if="client.plan != 'Enterprise'"/>
            <div class="row h6" v-if="client.plan != 'Enterprise'">
              <div class="col-md-9">{{ $t('profile.client.billing.total_monthly_price') }}</div>
              <div class="col-md-3" v-if="featureToSubscription.module != 'admin' && featureToSubscription.module != 'validator' && featureToSubscription.module != 'member' && featureToSubscription.module != 'partition'">&euro; {{ featureToSubscription.price + client.subscription_total }}</div>
              <div class="col-md-3" v-if="featureToSubscription.module == 'admin'">&euro; {{ (adminsAmount - client.admin_limit) * adminFeature.price + client.subscription_total }}</div>
              <div class="col-md-3" v-if="featureToSubscription.module == 'member'">&euro; {{ (membersAmount - client.member_limit) * memberFeature.price + client.subscription_total }}</div>
              <div class="col-md-3" v-if="featureToSubscription.module == 'validator'">&euro; {{ (validatorsAmount - client.validator_limit) * validatorFeature.price + client.subscription_total }}</div>
              <div class="col-md-3" v-if="featureToSubscription.module == 'partition'">&euro; {{ (partitionsAmount - client.partition_limit) * partitionFeature.price + client.subscription_total }}</div>
            </div>
            <div class="mt-5"  v-if="client.plan != 'Enterprise'">
              <b-button @click="confirmSubscriptionFeature(featureToSubscription)"
                        class="mt-3 w-100"
                        :class="{'btn-disabled': membersAmount == client.member_limit}"
                        variant="success"
                        qid="delete-question-submit-button"
                        v-if="featureToSubscription.module == 'member'"
              >
                {{ $t('profile.client.billing.checkout_label') }}
              </b-button>
              <b-button @click="confirmSubscriptionFeature(featureToSubscription)"
                        class="mt-3 w-100"
                        :class="{'btn-disabled': partitionsAmount == client.partition_limit}"
                        variant="success"
                        qid="delete-question-submit-button"
                        v-if="featureToSubscription.module == 'partition'"
              >
                {{ $t('profile.client.billing.checkout_label') }}
              </b-button>
              <b-button @click="confirmSubscriptionFeature(featureToSubscription)"
                        class="mt-3 w-100"
                        :class="{'btn-disabled': adminsAmount == client.admin_limit}"
                        variant="success"
                        qid="delete-question-submit-button"
                        v-if="featureToSubscription.module == 'admin'"
              >
                {{ $t('profile.client.billing.checkout_label') }}
              </b-button>
              <b-button @click="confirmSubscriptionFeature(featureToSubscription)"
                        class="mt-3 w-100"
                        :class="{'btn-disabled': validatorsAmount == client.validator_limit}"
                        variant="success"
                        qid="delete-question-submit-button"
                        v-if="featureToSubscription.module == 'validator'"
              >
                {{ $t('profile.client.billing.checkout_label') }}
              </b-button>

              <b-button @click="confirmSubscriptionFeature(featureToSubscription)"
                        class="mt-3 w-100"
                        variant="success"
                        qid="delete-question-submit-button"
                        v-if="featureToSubscription.module != 'admin' && featureToSubscription.module != 'validator' && featureToSubscription.module != 'member' && featureToSubscription.module != 'partition'"
              >
                {{ $t('profile.client.billing.checkout_label') }}
              </b-button>
            </div>
            <div class="mt-1"  v-if="client.plan == 'Enterprise'">
              <b-button @click="goToContactPage()"
                        class="mt-3 w-100"
                        variant="success"
                        qid="delete-question-submit-button"
              >
                {{ $t('profile.client.billing.contact_us_label') }}
              </b-button>
            </div>
          </div>
        </div>
      </div>

    </b-modal>


    <!-- Crop image modal -->
    <ImageCropDialog ref="cropDialog"
                     :title="$t('client_page.crop.title')"
                     :hint="$t('client_page.crop.hint')"
                     :save-label="$t('client_page.crop.save')"
                     :saving="buttonDisabled"
                     @crop="saveLogo"/>

  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js'
import {config} from '@/config.js'
import draggable from 'vuedraggable'
import Avatar from '@/components/Avatar.vue';
import ImageCropDialog from '@/components/ImageCropDialog.vue';
import ClientAiUsage from '@/components/client/ClientAiUsage.vue';
import ClientEmailSettings from '@/components/client/ClientEmailSettings.vue';
import moment from 'moment';

// Icon and status colour of the current plan (US 9391)
const PLAN_ICONS = {
  'Article 30 package': 'rocket',
  'Privacy package': 'space-shuttle',
  'Enterprise': 'building'
};
const PLAN_STATUS_TAGS = {
  trial: 'v2-tag--amber',
  trial_expired: 'v2-tag--red',
  subscription: 'v2-tag--teal',
  subscription_canceled: 'v2-tag--amber',
  subscription_ended: 'v2-tag--red',
  enterprise: 'v2-tag--violet'
};
import roleCheck from "@/util/ACL/roleCheck"



export default {
  name: 'ClientProfile',
  data: function() {
    return {
      breadcrumbItems: [
        {
          text: this.$t('dashboard.title'),
          to: '/'
        }
      ],
      clientForm: {
        name: '',
        cvr: '',
        street: '',
        city: '',
        state: '',
        postcode: '',
        country: '',
        email: '',
        reference_number: '',
        website:''
      },
      clientLogoForm: {
        file: {}
      },
      countryForm: {
        country: ''
      },
      reportForm: {
        name: '',
        ctid: '',
        reportId: '',
        pageName: ''
      },
      domainForm: {
        name: ''
      },
      features: [],
      plans: [],
      selectedPlan: {},
      riskTypeOptions: [
        { value: 'single_risk', name: 'Single risk' },
        { value: 'cia_risk', name: 'CIA risk' }
      ],
      tableItems: [],
      loading: true,
      error: '',
      buttonDisabled: false,
      client: {},
      section: 'overview',
      billingPage: 'plan',
      prefPage: 'notifications',
      usage: null,
      showRiskForm: false,
      selectedRiskType: [],
      showPowerBiForm: false,
      reportToken: '',
      itemToDelete: {},
      featureToDelete: {},
      featureToSubscription: {},
      itemToRefresh: {},
      selectedToken: '',
      selectedReport: {},
      domainTableItems: [],
      selectedDomain: [],
      totalPrice: 0,
      validatorFeature: {},
      memberFeature: {},
      adminFeature: {},
      partitionFeature: {},

      adminsAmount: 0,
      membersAmount: 0,
      validatorsAmount: 0,
      partitionsAmount: 0,

      avatar: '',
      avatarLarge: '',

      selectedFeatures: [],
      selectedModalFeatures: [],
      modalFeatures: [],
      selectedFeaturesIds: [],
      checkoutDisabled: false,
      userMessage: '',
      countries: [],
      selectedCountry: [],
      invoices: {},
      loadingInvoices: false,
      size: 'xl',
      itemToEnterprise: {}
    }
  },
  computed: {
    getClient() {
      return this.$store.state.client
    },
    getUser() {
      return this.$store.state.user
    },
    getTotalPrice() {
      return this.totalPrice
    },
    getClientAvatar(){
      let client = this.client;
      if (client.logo) {
        return client.logo;
      } else {
        return require('@/assets/tp-logo.png');
      }
    },
    getToken() {
      return this.$store.state.token
    },
    tabs() {
      return this.client.parent ? ['overview', 'preferences'] : ['overview', 'billing', 'preferences'];
    },
    isCurrentWorkspace() {
      return !!this.getClient && this.getClient.id === this.client.id;
    },
    parentIsMine() {
      let parent = this.client.parent;
      return !!parent && !!_.find(this.getUser.clients, { id: parent.id });
    },
    // AI usage and member counts need admin rights on the client
    canSeeUsage() {
      return !this.client.parent && this.checkIfAdmin();
    },
    planName() {
      if (this.client.subscription_status === 'enterprise') {
        return this.$t('client_page.stats.enterprise');
      }
      return (this.client.plan && this.client.plan.name) || '–';
    },
    usageRows() {
      return (this.usage && this.usage.workspaces) || [];
    },
    partitionRows() {
      return _.sortBy(this.usageRows.filter(row => !row.is_client), row => row.name.toLowerCase());
    },
    totalMembers() {
      return this.usage ? this.usage.members_total : '–';
    },
    ownMembers() {
      let row = _.find(this.usageRows, { id: this.client.id });
      return row ? row.members : null;
    },
    tokensThisMonth() {
      return this.usageRows.reduce((total, row) => total + row.total_tokens, 0);
    },
    detailFields() {
      let client = this.client;
      let place = [client.postcode, client.city].filter(Boolean).join(' ');
      return [
        { key: 'name', label: 'administration.clients.create.name_label', value: client.name },
        { key: 'street', label: 'administration.clients.create.street_label', value: client.street },
        { key: 'place', label: 'client_page.details.postcode_city', value: place },
        { key: 'state', label: 'administration.clients.create.state_label', value: client.state },
        { key: 'country', label: 'administration.clients.create.country_label', value: client.country },
        { key: 'cvr', label: 'administration.clients.create.cvr_label', value: client.cvr },
        { key: 'reference_number', label: 'administration.partitions.create.reference_label', value: client.reference_number },
        { key: 'website', label: 'administration.partitions.create.website_label', value: client.website }
      ];
    },
    modules() {
      return _.uniqBy(this.client.activeModules || [], 'code');
    },
    planDisplayName() {
      let plan = this.client.plan;
      return (plan && plan.name) || (typeof plan === 'string' ? plan : '') || this.planName;
    },
    planIcon() {
      return PLAN_ICONS[this.planDisplayName] || 'file-invoice';
    },
    planStatusTag() {
      return PLAN_STATUS_TAGS[this.client.subscription_status] || 'v2-tag--grey';
    },
    // When the plan started or ends, depending on its status
    planDateText() {
      let client = this.client;
      let subscription = client.subscription || {};
      let texts = {
        trial: ['profile.client.billing.expires_on', client.trial_ends_at],
        trial_expired: ['profile.client.billing.expired_at', client.trial_ends_at],
        subscription: ['profile.client.billing.activated_on', subscription.created_at],
        subscription_canceled: ['profile.client.billing.expires_on', subscription.ends_at],
        subscription_ended: ['profile.client.billing.ended_at', subscription.ends_at]
      };
      let text = texts[client.subscription_status];
      return text && text[1] ? this.$t(text[0]) + ' ' + this.formatDate(text[1]) : '';
    },
    showOwnedFeatures() {
      return (this.client.features || []).length && ['trial_expired', 'subscription_ended'].indexOf(this.client.subscription_status) === -1;
    },
    missingFeatures() {
      let features = this.client.missing_features || [];
      if (this.client.subscription_status === 'enterprise') {
        return features.filter(feature => !this.isSeatFeature(feature));
      }
      return ['trial_expired', 'subscription_ended', 'trial'].indexOf(this.client.subscription_status) === -1 ? features : [];
    }
  },
  watch: {
    getClient: function (newValue, oldValue) {
      if (newValue && oldValue && newValue.slug && oldValue.slug) {
        if (this.$route.name == 'administration.clientView') {
          this.$router.push('/')
        } else {
          this.$router.push({ name: 'clientProfile', params: { slug: newValue.slug }})
        }
        this.avatarLarge = this.getClientAvatar;
      }
    },
  },
  components: {
    Avatar,
    ImageCropDialog,
    ClientAiUsage,
    ClientEmailSettings,
    draggable
  },
  methods: {
    initials(name) {
      let words = (name || '').trim().split(/\s+/).filter(Boolean);
      return words.slice(0, 2).map(word => word.charAt(0)).join('').toUpperCase();
    },
    formatDate(date) {
      return moment(date).format(config.dateFormat);
    },
    monthName() {
      return moment().format('MMMM');
    },
    // 3,420,000 -> 3.42M, 52,000 -> 52K
    formatTokens(value) {
      if (value >= 1000000) {
        return (value / 1000000).toFixed(2).replace(/\.?0+$/, '') + 'M';
      }
      if (value >= 1000) {
        return Math.round(value / 1000) + 'K';
      }
      return String(value || 0);
    },
    moduleName(module) {
      let key = 'client_page.modules.' + module.code;
      return this.$te(key) ? this.$t(key) : module.name;
    },
    // This month's AI usage and members of the client and its partitions, for the hero and the Partitions card
    // Only admins may read it; the server says no to the rest, and then the hero just shows dashes
    loadUsage() {
      piincHttp.get('clients/' + this.client.slug + '/ai-usage').then(response => {
        this.usage = response.data;
      }, function() {});
    },
    loadCountries() {
      let self = this;
      piincHttp.get('countries-with-flag').then(function(response) {
        self.countries = response.data

        if (self.client.country) {
          _.find(self.countries, function(item) {
            if (item.name === self.client.country) {
              self.client.country_with_flag = item
            }
          });
        }

      }, function() {});
    },
    // Uploads the cropped logo. The sidebar shows the current workspace's logo, so that needs a fresh page
    saveLogo(file) {
      this.buttonDisabled = true;

      let formData = new FormData();
      formData.append('logo', file, 'logo.png');
      formData.append('_method', 'put');

      piincHttp.post('clients/' + this.client.slug, formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(() => {
        if (this.isCurrentWorkspace) {
          window.location.reload();
          return;
        }
        this.buttonDisabled = false;
        this.$refs.cropDialog.close();
        this.loadClient();
      }, error => {
        this.buttonDisabled = false;
        let data = (error && error.data) || {};
        alert((data.message || '') + (data.errors && data.errors.logo ? ' ' + data.errors.logo[0] : ''));
      });
    },
    onFileChange(event) {
      let file = event.target.files[0];
      event.target.value = '';

      if (!file) {
        return;
      }
      if (file.size > 10485760) {
        alert(this.$t('system.file_size'));
        return;
      }

      this.buttonDisabled = false;
      this.$refs.cropDialog.open(file);
    },
    isSeatFeature(feature) {
      return ['admin', 'member', 'validator', 'partition'].indexOf(feature.module) !== -1;
    },
    pickRiskType(option) {
      this.selectedRiskType = option;
      this.showRiskForm = option.value !== this.client.risk_type;
    },
    copyText(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$toastr('success', this.$t('client_page.reporting.copied'));
      }, function() {});
    },
    removePhoto() {
      let self = this;
      let formData = new FormData();
      formData.append("logo", null);
      formData.append("_method", 'put');

      piincHttp.post('clients/' + this.client.slug, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }).then(function(response) {
        self.client = response.data
        window.location.reload()
        self.avatar = '';
      }, function(error) {})
    },
    resetFile() {
      let imagefile = document.querySelector('#file-photo');
      imagefile.value = '';
    },
    showConfirmEnterpriseModal(object) {
      this.itemToEnterprise = object
      this.$refs['enterprise-client'].show()
    },
    hideConfirmEnterpriseModal() {
      this.itemToEnterprise = {};
      this.$refs['enterprise-client'].hide()
    },
    confirmEnterpriseClient(client) {
      let self = this;
      piincHttp.post('clients/' + client.slug + '/enterprise').then(function(response) {
        self.hideConfirmEnterpriseModal();
        self.breadcrumbItems.pop();
        self.loadClient();
        // Trigger toastr message after modal close
        self.$toastr('success', self.$t('administration.clients.enterprise.success'))
      }, function() {

      });
    },
    showPowerBiFormFunction() {
      this.showPowerBiForm = true;
      let self = this;
      self.reportToken = self.client.report_token;
    },
    goToContactPage() {
      window.open('https://complyon.com/contact-form/', '_blank')
    },
    checkout() {
      this.checkoutDisabled = true
      this.$refs.form.submit()
    },
    isAvailable(feature) {
      let available = false;

      if (feature.module == 'gdpr_advanced') {
        if (this.modalFeatures[2].selected) {
          available = true
        }
      }
      if (feature.module == 'contract_advanced') {
        if (this.modalFeatures[4].selected) {
          available = true
        }
      }
      if (feature.module == 'assessment') {
        if (this.modalFeatures[6].selected) {
          available = true
        }
      }

      return available;
    },

    decreaseMembers() {
      if (this.membersAmount > 0) {
        if(this.client.user_counts.member < this.membersAmount){
          this.membersAmount -= 1
          this.totalPrice -= this.memberFeature.price
          this.userMessage = '';
        }else{
          this.userMessage = this.$t('profile.client.billing.user_message', { count: this.client.user_counts.member, role: "Member" })
        }
      }
    },
    increaseMembers() {
      this.membersAmount += 1
      this.totalPrice += this.memberFeature.price
    },
    decreaseAdmins() {
      if (this.adminsAmount > 1) {
        if(this.client.user_counts.admin < this.adminsAmount){
          this.adminsAmount -= 1
          this.totalPrice -= this.adminFeature.price
          this.userMessage = '';
        }else{
          this.userMessage = this.$t('profile.client.billing.user_message', { count: this.client.user_counts.admin, role: "Admin" })
        }
      }
    },
    increaseAdmins() {
      this.adminsAmount += 1
      this.totalPrice += this.adminFeature.price
    },
    decreaseValidators() {
      if (this.validatorsAmount > 0) {
        if (this.client.user_counts.validator < this.validatorsAmount) {
          this.validatorsAmount -= 1
          this.totalPrice -= this.validatorFeature.price
          this.userMessage = '';
        } else {
          this.userMessage = this.$t('profile.client.billing.user_message', { count: this.client.user_counts.validator, role: "Validator" })
        }
      }
    },
    increaseValidators() {
      this.validatorsAmount += 1
      this.totalPrice += this.validatorFeature.price
    },
    decreasePartitions() {
      if (this.partitionsAmount > 0) {
        if (this.client.user_counts.partition < this.partitionsAmount) {
          this.partitionsAmount -= 1
          this.totalPrice -= this.partitionFeature.price
          this.userMessage = '';
        } else {
          this.userMessage = this.$t('profile.client.billing.partition_message', { count: this.client.user_counts.partition })
        }
      }
    },
    increasePartitions() {
      this.partitionsAmount += 1
      this.totalPrice += this.partitionFeature.price
    },
    showUnsubscribe() {
      let self = this;
      this.$refs['delete-subscription'].show()
    },
    hideUnsubscribe() {
      let self = this;
      this.$refs['delete-subscription'].hide()
    },
    unsubscribeFunction() {
      let self = this;
      piincHttp.delete('clients/' + this.$route.params.slug + '/subscription').then(function() {
        self.$refs['delete-subscription'].hide();
        self.$refs['delete-subscription-success'].show();
      }, function() {});
    },
    continueDeleteSubscription(){
      let self = this;
      this.$refs['delete-subscription-success'].hide()
      window.location.href = '/client/' + this.client.slug + '?tabindex=1';
    },
    confirmDeleteSubscription(object) {
      let self = this;
      piincHttp.delete('clients/' + this.$route.params.slug + '/features/'+object.id).then(function() {
        self.$refs['delete-feature'].hide();
        self.$refs['delete-feature-success'].show();
      }, function() {});
    },
    checkIfAdmin() {
      if (roleCheck('Client admin') || roleCheck('Partition admin') || this.getUser.isAdmin) {
        return true
      } else {
        return false
      }
    },
    cancelRiskForm() {
      this.showPowerBiForm = false;
      let self = this;
      self.reportToken = '';
    },
    formRiskTypeSubmit() {
      let self = this;
      piincHttp.put('clients/' + this.$route.params.slug, {
        report_token: this.reportToken ? this.reportToken : ''
      }).then(function(response) {
        self.client = response.data;
        self.showPowerBiForm = false;
      }, function(error) {
        if (error.status !== 200) {
          self.buttonDisabled = false;
          self.error = error.data.message;
        }
      })
    },
    reportFormSubmit(evt) {
      this.buttonDisabled = true;
      let formData = new FormData();

      // Keep the reference in a variable
      // to use it inside function called when promise returned
      let self = this;
      evt.preventDefault();

      formData.append("client", this.$route.params.slug);
      formData.append("name", this.reportForm.name);
      formData.append("ctid", this.reportForm.ctid);
      formData.append("report_id", this.reportForm.reportId);
      formData.append("page_name", this.reportForm.pageName);

      piincHttp.post('dashboards', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }).then(function(response) {
        self.$toastr('success', self.$t('profile.client.report_create_success'))
        self.hideCreateReportModal();
        self.loadReports();
        self.buttonDisabled = false;
      }, function(error) {
        if (error.status !== 200) {
          self.buttonDisabled = false;
          self.error = error.data.message;
        }
      })
    },
    reportEditFormSubmit(evt) {
      this.buttonDisabled = true;

      // Keep the reference in a variable
      // to use it inside function called when promise returned
      let self = this;
      evt.preventDefault();

      let form = {};

      form.name = this.reportForm.name
      form.ctid = this.reportForm.ctid
      form.report_id = this.reportForm.reportId
      form.page_name = this.reportForm.pageName

      piincHttp.put('dashboards/' + this.selectedReport.slug, form).then(function(response) {
        self.$toastr('success', self.$t('profile.client.report_edit_success'))
        self.hideEditReportModal();
        self.loadReports();
        self.buttonDisabled = false;
      }, function(error) {
        if (error.status !== 200) {
          self.buttonDisabled = false;
          self.error = error.data.message;
        }
      })
    },
    showCreateReportModal(object) {
      this.reportForm.name = '';
      this.reportForm.ctid = '';
      this.reportForm.reportId = '';
      this.reportForm.pageName = '';
      this.$refs['create-report'].show()
    },
    hideCreateReportModal() {
      this.reportForm.name = '';
      this.reportForm.ctid = '';
      this.reportForm.reportId = '';
      this.reportForm.pageName = '';
      this.$refs['create-report'].hide()
    },
    showEditReportModal(object) {
      this.selectedReport = object
      this.reportForm.name = object.name;
      this.reportForm.ctid = object.ctid;
      this.reportForm.reportId = object.report_id;
      this.reportForm.pageName = object.page_name;
      this.$refs['edit-report'].show()
    },
    hideEditReportModal() {
      this.selectedReport = {};
      this.reportForm.name = '';
      this.reportForm.ctid = '';
      this.reportForm.reportId = '';
      this.reportForm.pageName = '';
      this.$refs['edit-report'].hide()
    },
    domainFormSubmit(evt) {
      this.buttonDisabled = true;
      let formData = new FormData();

      // Keep the reference in a variable
      // to use it inside function called when promise returned
      let self = this;
      evt.preventDefault();

      formData.append("domain", this.domainForm.name);

      piincHttp.post('clients/' + this.$route.params.slug + '/domains', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }).then(function(response) {
        self.$toastr('success', self.$t('profile.client.domain_create_success'))
        self.hideCreateDomainModal();
        self.loadDomains();
        self.buttonDisabled = false;
      }, function(error) {
        if (error.status !== 200) {
          self.buttonDisabled = false;
          self.domainForm.error = error.data.message;
          if (error.data.errors.domain) {
            self.domainForm.error = self.domainForm.error + '\n\n' + error.data.errors.domain[0];
          }
        }
      })
    },
    domainEditFormSubmit(evt) {
      this.buttonDisabled = true;

      // Keep the reference in a variable
      // to use it inside function called when promise returned
      let self = this;
      evt.preventDefault();

      let form = {};

      form.domain = this.domainForm.name

      piincHttp.put('clients/' + this.$route.params.slug + '/domains/' + this.selectedDomain.id, form).then(function(response) {
        self.$toastr('success', self.$t('profile.client.domain_edit_success'))
        self.hideEditDomainModal();
        self.loadDomains();
        self.buttonDisabled = false;
      }, function(error) {
        if (error.status !== 200) {
          self.buttonDisabled = false;
          self.error = error.data.message;
          if (error.data.errors.domain) {
            self.domainForm.error = self.domainForm.error + '\n\n' + error.data.errors.domain[0];
          }
        }
      })
    },
    showCreateDomainModal(object) {
      this.domainForm.name = '';
      this.domainForm.error = '';
      this.$refs['create-domain'].show()
    },
    hideCreateDomainModal() {
      this.domainForm.name = '';
      this.domainForm.error = '';
      this.$refs['create-domain'].hide()
    },
    showEditDomainModal(object) {
      this.selectedDomain = object
      this.domainForm.name = object.domain;
      this.domainForm.error = '';
      this.$refs['edit-domain'].show()
    },
    hideEditDomainModal() {
      this.domainForm.name = '';
      this.domainForm.error = '';
      this.selectedDomain = []
      this.$refs['edit-domain'].hide()
    },
    showRefreshClientModal(object) {
      this.itemToRefresh = object
      this.$refs['refresh-client'].show()
    },
    hideRefreshClientModal() {
      this.itemToRefresh = {};
      this.$refs['refresh-client'].hide()
    },
    showDomainConfirmDeleteModal(object) {
      this.itemToDelete = object
      this.$refs['delete-domain'].show()
    },
    hideDomainConfirmDeleteModal() {
      this.itemToDelete = {};
      this.$refs['delete-domain'].hide()
    },
    confirmDeleteDomain(object) {
      let self = this;

      piincHttp.delete('clients/' + this.$route.params.slug + '/domains/' + object.id).then(function() {
        self.hideDomainConfirmDeleteModal();
        self.loadDomains();
        // Trigger toastr message after modal close
        self.$toastr('success', self.$t('profile.client.domain_delete_success'))
      }, function() {

      });
    },
    showConfirmDeleteModal(object) {
      this.itemToDelete = object
      this.$refs['delete-client'].show()
    },
    hideConfirmDeleteModal() {
      this.itemToDelete = {};
      this.$refs['delete-client'].hide()
    },
    confirmDeleteClient(client) {
      let self = this;

      piincHttp.put('clients/' + this.$route.params.slug, {
        api_token: ''
      }).then(function(response) {
        self.client = response.data;
        self.hideConfirmDeleteModal();
        // Trigger toastr message after modal close
        self.$toastr('success', self.$t('administration.clients.delete.token_success'))
      }, function() {

      });
    },
    showClientTokenModal(object) {
      this.selectedToken = object
      this.$refs['client-token'].show()
    },
    hideClientTokenModal() {
      this.selectedToken = '';
      this.$refs['client-token'].hide()
    },
    showCartModal(object) {
      let self = this;
      this.selectedPlan = object
      this.checkoutDisabled = false;
      this.featureToSubscription = {}

      _.find(this.features, function(item) {
        if (item.module === 'admin') {
          self.adminFeature = item;
        }
      });
      _.find(this.features, function(item) {
        if (item.module === 'partition') {
          self.partitionFeature = item;
        }
      });
      _.find(this.features, function(item) {
        if (item.module === 'member') {
          self.memberFeature = item;
        }
      });
      _.find(this.features, function(item) {
        if (item.module === 'validator') {
          self.validatorFeature = item;
        }
      });

      this.totalPrice = this.selectedPlan.price
      if(!this.client.country){
        this.size = 'xs';
      }
      this.$refs['cart-modal'].show()
    },
    showResume(object) {
      let self = this;
      this.selectedPlan = object
      this.checkoutDisabled = false;

      this.$refs['resume'].show()
    },
    hideResume(object) {
      let self = this;
      this.selectedPlan = object
      this.checkoutDisabled = false;

      this.$refs['resume'].hide()
    },
    confirmResume(object) {
      let self = this;
      piincHttp.post('clients/' + this.$route.params.slug + '/subscription/resume').then(function() {
        self.$refs['resume'].hide();
        self.$refs['resume-success'].show();
      }, function() {});
    },
    continueResume(){
      let self = this;
      this.selectedPlan = {}
      this.$refs['resume-success'].hide()
      window.location.href = '/client/' + this.client.slug + '?tabindex=1';
    },
    hideCartModal() {
      this.selectedPlan = '';
      this.selectedFeaturesIds = [];
      this.selectedFeatures = [];
      this.totalPrice = 0;

      this.adminsAmount = this.client.admin_limit;
      this.membersAmount = this.client.member_limit;
      this.validatorsAmount = this.client.validator_limit;
      this.partitionsAmount = this.client.partition_limit;
      this.$refs['cart-modal'].hide()
    },
    showModulesModal() {
      let self = this;
      this.modalFeatures = [];
      this.selectedModalFeatures = []

      _.forEach(this.features, function (feature, index) {
        feature.selected = false
        feature.from_plan = false;
      })

      _.forEach(this.features, function (feature, index) {
        if (feature.module == 'processing_activities') {
          self.modalFeatures.push(feature)
        }
      })
      _.forEach(this.features, function (feature, index) {
        if (feature.module == 'system') {
          self.modalFeatures.push(feature)
        }
      })
      _.forEach(this.features, function (feature, index) {
        if (feature.module == 'gdpr') {
          self.modalFeatures.push(feature)
        }
      })
      _.forEach(this.features, function (feature, index) {
        if (feature.module == 'gdpr_advanced') {
          self.modalFeatures.push(feature)
        }
      })
      _.forEach(this.features, function (feature, index) {
        if (feature.module == 'contract_basic') {
          self.modalFeatures.push(feature)
        }
      })
      _.forEach(this.features, function (feature, index) {
        if (feature.module == 'contract_advanced') {
          self.modalFeatures.push(feature)
        }
      })
      _.forEach(this.features, function (feature, index) {
        if (feature.module == 'project') {
          self.modalFeatures.push(feature)
        }
      })
      _.forEach(this.features, function (feature, index) {
        if (feature.module == 'assessment') {
          self.modalFeatures.push(feature)
        }
      })
      _.forEach(this.features, function (feature, index) {
        if (feature.module == 'policy') {
          self.modalFeatures.push(feature)
        }
      })
      _.forEach(this.features, function (feature, index) {
        if (feature.module == 'risk_control') {
          self.modalFeatures.push(feature)
        }
      })

      this.selectedModalFeatures =  _.cloneDeep(this.selectedFeatures);

      _.forEach(this.modalFeatures, function (feature, index) {
        _.find(self.client.features, function(planFeature) {
          if (planFeature.id == feature.id) {
            feature.from_plan = true;
            feature.selected = true;
          }
        });
        _.find(self.selectedModalFeatures, function(modalFeature) {
          if (modalFeature.id == feature.id) {
            feature.selected = true;
          }
        });
        self.$set(self.modalFeatures, index, feature)
      })

      this.$refs['modules-modal'].show()
    },
    hideModulesModal() {
      this.selectedModalFeatures = [];
      this.$refs['modules-modal'].hide()
    },
    selectFeature(index, item) {
      let self = this;
      item.selected = item.selected ? false : true;
      if (item.selected) {
        this.selectedModalFeatures.push(item)
      } else {
        let itemIndex = _.findIndex(this.selectedModalFeatures, function(o) { return o.id == item.id; });
        this.selectedModalFeatures.splice(itemIndex, 1);
      }

      this.$set(this.modalFeatures, index, item)
    },
    addFeatures() {
      let self = this;

      _.forEach(this.selectedModalFeatures, function (feature, index) {
        if (_.findIndex(self.selectedFeatures, function(o) { return o.id == feature.id }) == -1) {
          self.totalPrice += feature.price
        }
      })

      _.forEach(this.selectedFeatures, function (feature, index) {
        if (_.findIndex(self.selectedModalFeatures, function(o) { return o.id == feature.id }) == -1) {
          self.totalPrice -= feature.price
        }
      })

      this.selectedFeatures = _.cloneDeep(this.selectedModalFeatures);

      _.forEach(this.selectedFeatures, function (feature, index) {
        self.selectedFeaturesIds.push(feature.id)
      })
      this.hideModulesModal()
    },
    removeFeature(index, feature) {
      this.selectedFeatures.splice(index, 1);
      this.totalPrice -= feature.price;
    },
    showReportConfirmDeleteModal(object) {
      this.itemToDelete = object
      this.$refs['delete-report'].show()
    },
    hideReportConfirmDeleteModal() {
      this.itemToDelete = {}
      this.$refs['delete-report'].hide()
    },
    showFeatureConfirmDeleteModal(object) {
      this.featureToDelete = object
      this.$refs['delete-feature-success'].hide();
      this.$refs['delete-feature'].show()
    },
    hideFeatureConfirmDeleteModal() {
      this.featureToDelete = {}
      this.$refs['delete-feature'].hide()
    },
    showFeatureConfirmSubscriptionModal(object) {
      this.adminsAmount = this.client.admin_limit;
      this.membersAmount = this.client.member_limit;
      this.validatorsAmount = this.client.validator_limit;
      this.partitionsAmount = this.client.partition_limit;
      this.featureToSubscription = object
      this.userMessage = ''
      this.$refs['subscription-feature-success'].hide();
      this.$refs['subscription-feature'].show()
    },
    hideFeatureConfirmSubscriptionModal() {
      this.featureToSubscription = {}
      this.$refs['subscription-feature'].hide()
    },
    showCheckoutSuccessModal() {
      this.$refs['checkout-success'].show()
    },
    hideCheckoutSuccessModal() {
      this.featureToSubscription = {}
      this.$refs['checkout-success'].hide()
    },
    confirmDeleteReport(object) {
      let self = this;
      piincHttp.delete('dashboards/' + object.slug).then(function() {
        self.hideReportConfirmDeleteModal();
        self.loadReports();
        // Trigger toastr message after modal close
        self.$toastr('success', self.$t('profile.client.report_delete_success'))
      }, function() {});
    },
    continueDeleteFeature(){
      let self = this;
      this.featureToDelete = {}
      this.$refs['delete-feature-success'].hide()
      window.location.href = '/client/' + this.client.slug + '?tabindex=1';
    },
    confirmDeleteFeature(object) {
      let self = this;
      piincHttp.delete('clients/' + this.$route.params.slug + '/features/'+object.id).then(function() {
        self.$refs['delete-feature'].hide();
        self.$refs['delete-feature-success'].show();
      }, function() {});
    },
    confirmSubscriptionFeature(object, quantity = 0) {
      let self = this;

      if (object.module === 'admin') {
        quantity = this.adminsAmount;
      } else if (object.module === 'member') {
        quantity = this.membersAmount;
      } else if (object.module === 'validator') {
        quantity = this.validatorsAmount;
      } else if (object.module === 'partition') {
        quantity = this.partitionsAmount;
      } else {
        quantity = 0
      }

      piincHttp.post('clients/' + this.$route.params.slug + '/features', {
        feature: object.id,
        quantity: quantity
      }).then(function() {
        self.$refs['subscription-feature'].hide();
        self.$refs['subscription-feature-success'].show();
      }, function() {});
    },
    continueSubscriptionFeature(){
      let self = this;
      this.featureToSubscription = {}
      this.$refs['subscription-feature-success'].hide()
      window.location.href = '/client/' + this.client.slug + '?tabindex=1';
    },
    confirmRefreshClient(client) {
      let self = this;

      piincHttp.get('clients/' + this.$route.params.slug + '/generate-token').then(function(response) {
        self.client = response.data.client;
        self.hideRefreshClientModal();
        self.showClientTokenModal(response.data.token);
        // Trigger toastr message after modal close
        self.$toastr('success', self.$t('administration.clients.refresh.success'))
      }, function() {

      });
    },
    postOrder() {
      let self = this;
      let order = []
      _.forEach(this.tableItems, function (item, index) {
        order.push({
          id: item.id,
          position: index + 1
        });
      })
      piincHttp.post('dashboards/order', {
        client: this.client.id,
        order: order
      }).then(function(response) {
      }, function(error) {
        if (error.status !== 200) {
          self.buttonDisabled = false;
          self.error = error.data.message;
        }
      })
    },
    showRiskFormFunction() {
      this.showRiskForm = true;
      let self = this;

      _.find(this.riskTypeOptions, function(item) {
        if (item.value === self.client.risk_type) {
          self.selectedRiskType = item;
        }
      });

    },
    cancelRiskForm() {
      this.showRiskForm = false;
      let self = this;

      _.find(this.riskTypeOptions, function(item) {
        if (item.value === self.client.risk_type) {
          self.selectedRiskType = item;
        }
      });
    },
    formRiskTypeSubmit() {
      let self = this;
      piincHttp.put('clients/' + this.client.slug, {
        risk_type: this.selectedRiskType ? this.selectedRiskType.value : ''
      }).then(function(response) {
        let client = response.data;
        self.buttonDisabled = false;

        if (self.client.slug != client.slug) {
          localStorage.removeItem('client')
          piincHttp.get('users/me/permissions/' + client.slug).then(function(response) {
            client.user_permissions = response.data.user_permissions
            client.user_roles = response.data.user_roles
            localStorage.setItem('clientObject', JSON.stringify(client));
            localStorage.setItem('client', client.slug);
            self.$store.commit('storeClient', client);
            self.showRiskForm = false;
            window.location.reload();
          }, function() {});
        } else {
          window.location.reload();
        }
      }, function(error) {
        if (error.status !== 200) {
          self.buttonDisabled = false;
          self.error = error.data.message;
        }
      })
    },
    showFormFunction() {
      let self = this;
      this.clientForm.name = this.client.name;
      this.clientForm.cvr = this.client.cvr;
      this.clientForm.reference_number = this.client.reference_number;
      this.clientForm.website = this.client.website;
      this.clientForm.street = this.client.street;
      this.clientForm.city = this.client.city;
      this.clientForm.state = this.client.state;
      this.clientForm.postcode = this.client.postcode;

      if (this.client.country) {
        this.selectedCountry = { name: this.client.country }
      }

      this.selectedCountry = [];
      if (this.client.country) {
        _.find(this.countries, function(item) {
          if (item.name === self.client.country) {
            self.selectedCountry = item
          }
        });
      }

      this.clientForm.country = this.client.country;
      this.clientForm.error = '';
      this.$refs['details-panel'].show();
    },
    hideFormFunction() {
      this.$refs['details-panel'].hide();
    },
    clientFormSubmit() {
      this.buttonDisabled = true;
      let self = this;

      this.clientForm.country = '';
      if (this.selectedCountry && this.selectedCountry.name) {
        this.clientForm.country = this.selectedCountry.name
      }

      if (this.selectedCountry && this.selectedCountry.name) {
        piincHttp.put('clients/' + this.client.slug, this.clientForm).then(function(response) {
          let client = response.data;
          self.buttonDisabled = false;
          self.hideFormFunction();
          self.breadcrumbItems.pop();
          if (self.$route.name == 'administration.clientView') {
            // Additional removing of breadcrumb items if administration
            self.breadcrumbItems.pop();
            self.breadcrumbItems.pop();
          }
          self.loadClient();

        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.clientForm.error = error.data.message;
          }
        })
      } else {
        this.clientForm.error = this.$t('system.country_error_message')
        this.buttonDisabled = false;
      }

    },
    countryFormSubmit() {
      this.buttonDisabled = true;
      let self = this;

      this.countryForm.country = '';
      if (this.selectedCountry && this.selectedCountry.name) {
        this.countryForm.country = this.selectedCountry.name
      }

      if (this.selectedCountry && this.selectedCountry.name) {
        piincHttp.put('clients/' + this.client.slug, this.countryForm).then(function(response) {
          let client = response.data;
          self.buttonDisabled = false;
          self.hideFormFunction();
          self.breadcrumbItems.pop();
          self.loadClient(true);
        }, function(error) {
          if (error.status !== 200) {
            self.buttonDisabled = false;
            self.clientForm.error = error.data.message;
          }
        })
      } else {
        this.clientForm.error = this.$t('system.country_error_message')
        this.buttonDisabled = false;
      }

    },
    loadReports() {
      let self = this;

      piincHttp.get('dashboards', { params:
        {
          page: 1,
          per_page: 1,
          client: this.client.slug
        }
      }).then(function(response) {
        let total = response.data.total_count

        piincHttp.get('dashboards', { params:
          {
            page: 1,
            per_page: total,
            client: self.client.slug
          }
        }).then(function(response) {
          self.loading = false;
          self.tableItems = response.data.items;
        }, function() {});
      }, function() {});
    },
    loadDomains() {
      let self = this;

      piincHttp.get('clients/' + this.$route.params.slug + '/domains', { params:
        {
          page: 1,
          per_page: 1
        }
      }).then(function(response) {
        self.domainTableItems = response.data.items
      }, function() {});
    },
    loadFeatures() {
      let self = this;

      piincHttp.get('features', { params: {
        per_page: 100
      }}).then(function(response) {
        self.features = response.data.items;

        _.find(self.features, function(item) {
          if (item.module === 'admin') {
            self.adminFeature = item;
          }
        });
        _.find(self.features, function(item) {
          if (item.module === 'partition') {
            self.partitionFeature = item;
          }
        });
        _.find(self.features, function(item) {
          if (item.module === 'member') {
            self.memberFeature = item;
          }
        });
        _.find(self.features, function(item) {
          if (item.module === 'validator') {
            self.validatorFeature = item;
          }
        });

      }, function() {});
    },
    loadPlans() {
      let self = this;

      piincHttp.get('plans', { params: {
        per_page: 100
      }}).then(function(response) {
        self.plans = response.data.items;
      }, function() {});
    },
    loadClient(forceBigModal) {
      let self = this;

      piincHttp.get('clients/' + this.$route.params.slug, { params:
        {
          full: 1
        }
      }).then(function(response) {
        self.client = response.data
        self.membersAmount = self.client.member_limit;
        self.adminsAmount = self.client.admin_limit;
        self.validatorsAmount = self.client.validator_limit;
        self.partitionsAmount = self.client.partition_limit;

        if (forceBigModal) {
          self.size = 'xl';
        }

        self.loadReports()
        self.loadUsage()

        if (self.countries && self.countries.length) {
          if (self.client.country) {
            _.find(self.countries, function(item) {
              if (item.name === self.client.country) {
                self.client.country_with_flag = item
              }
            });
          }
        }

        if (self.$route.name == 'administration.clientView') {
          self.breadcrumbItems.push({
            text: self.$t('administration.title'),
            to: '/administration'
          })
          self.breadcrumbItems.push({
            text: self.$t('administration.clients.title'),
            to: '/administration/clients'
          })
          self.breadcrumbItems.push({text: self.client.name, active: true});
        } else {
          if (self.client.parent) {
            self.breadcrumbItems.push({
              text: self.$t('profile.partition_profile'),
              active: true
            })
          } else {
            self.breadcrumbItems.push({
              text: self.$t('profile.client_profile'),
              active: true
            })
          }
        }

      }, function() {});
    },
    loadClientInvoices() {
      let self = this;
      this.loadingInvoices = true;

      piincHttp.get('clients/' + this.$route.params.slug + '/invoices').then(function(response) {
        self.invoices = response.data
        self.loadingInvoices = false;
      }, function() {});
    },
    checkAdmin(role) {
      let hasRole = false;
      let user = this.$store.state.user;
      if (user.isAdmin) {
        hasRole = true;
      }
      // Return Boolean if user has given role
      return hasRole;
    }
  },
  created () {
    let self = this;

    setTimeout(function() {
      // Old links open Billing with ?tabindex=1
      if (self.$route.query.tabindex == 1) {
        self.section = 'billing'
      }
    }, 500);

    setTimeout(function() {
      if (self.$route.query.modal && self.$route.query.modal == 'success') {
        self.showCheckoutSuccessModal()
      }
    }, 800);


    this.loadClient()
    this.loadFeatures()
    this.loadPlans()
    this.loadDomains()
    this.loadCountries()
    this.loadClientInvoices();
  }
}
</script>
